import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Calendar, MapPin, Upload, CheckCircle2, AlertCircle, Plus, X, Maximize2, Trash2, Eye, PencilIcon, Search, ArrowUpCircle, RefreshCw, Save, ArrowUp, RotateCw, ArrowLeft, Pencil, FileText, Copy, ChevronRight, Share2, DollarSign, Users, ChevronDown, Link as LinkIcon, ExternalLink } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Toaster, toast } from 'react-hot-toast';
import DatePicker from 'react-datepicker';
import imageCompression from 'browser-image-compression';
import { PDFDocument } from 'pdf-lib';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, getDocs, query, orderBy, limit, deleteDoc, doc, updateDoc, startAfter, onSnapshot, getDoc, where, writeBatch, serverTimestamp, runTransaction, arrayUnion, arrayRemove, setDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject, uploadBytesResumable, updateMetadata } from 'firebase/storage';
import { db, storage } from '../utils/firebase';
import { portalDb } from '../utils/portalFirebase';
import 'react-datepicker/dist/react-datepicker.css';
import PrewrittenNotes from './PrewrittenNotes';
import { APP_VERSION } from '../utils/version';
import ErrorBoundary from './ErrorBoundary';
import ImageEditor from './ImageEditor';
import Header from './Header';
import { auth } from '../utils/firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { FirebaseError } from 'firebase/app';
import MobileActionBar from './MobileActionBar';
import ViewCountModal from './ViewCountModal';
import ChangelogModal from './ChangelogModal';
import ClientSearch from './ClientSearch';
import { notifyReportRemoved, notifyReportShared } from '../utils/portalSync';
import ReportResources from './ReportResources';
import AdminReportModal from './AdminReportModal';
import InspectorSearch from './InspectorSearch';
import { getFirestore } from 'firebase/firestore';
import FormPreviewModal from './FormPreviewModal';
import DraftsSection from './DraftsSection';

/* ===================== Utility Functions ===================== */
const saveToLocalStorage = (data) => {
  localStorage.setItem('tankFormData', JSON.stringify(data));
};

const loadFromLocalStorage = () => {
  const saved = localStorage.getItem('tankFormData');
  return saved ? JSON.parse(saved) : null;
};

/* ===================== Main Component ===================== */
const ModernForm = () => {
  const [user, loading] = useAuthState(auth);
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState(() => {
    const saved = loadFromLocalStorage();
    if (saved) {
      // Convert the saved date string back to a proper date
      saved.dateOfInspection = saved.dateOfInspection || new Date().toISOString().split('T')[0];
      return saved;
    }
    return {
      clientName: '',
      dateOfInspection: new Date().toISOString().split('T')[0],
      propertyAddress: '',
      inspectionType: 'standard', // "standard" | "gpr"
      gprScope: 'full',           // "full" | "targeted" (only when inspectionType === 'gpr')
      gprTargetedLocation: '',    // required when gprScope === 'targeted'
      gprTargetedReason: '',     // metal_detection | visual | prior_docs | client_request | other
      gprTargetedReasonOther: '', // when gprTargetedReason === 'other'
      scenario: 'noTank',
      anomalyLocation: '',
      customLocation: '',
      customText: '',
      limitations: [],
      findings: {
        vent: false,
        fill: false,
        distribution: false,
      },
      fullDocumentText: '',
      imageUrls: [], // Add this to store Firebase Storage URLs
      resources: [], // Add resources field to formData
    };
  });

  // Add state for autosave preference
  const [autosaveEnabled, setAutosaveEnabled] = useState(true);
  const [autoPdfEnabled, setAutoPdfEnabled] = useState(true);
  const [defaultPaymentStatus, setDefaultPaymentStatus] = useState(true); // Default to paid reports

  // Load user preferences
  useEffect(() => {
    const loadUserPreferences = async () => {
      if (user?.uid) {
        try {
          const userPrefsDoc = await getDoc(doc(db, 'userPreferences', user.uid));
          if (userPrefsDoc.exists()) {
            const prefs = userPrefsDoc.data();
            setAutosaveEnabled(prefs.autosaveEnabled ?? true);
            setAutoPdfEnabled(prefs.autoPdfEnabled ?? true);
            setDefaultPaymentStatus(prefs.defaultPaymentStatus ?? true); // Load default payment status
          }
        } catch (error) {
          console.error('Error loading user preferences:', error);
        }
      }
    };
    
    loadUserPreferences();
  }, [user]);

  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [imageUploadProgress, setImageUploadProgress] = useState({}); // Add this to track upload progress
  const addressInputRef = useRef(null);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [recentReports, setRecentReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewDraft, setPreviewDraft] = useState(null);
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [isLoadingReport, setIsLoadingReport] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingReportId, setEditingReportId] = useState(null);
  // Revision of the report as loaded for editing; saves refuse to overwrite a newer revision (Horizon or another inspector)
  const [editingRevision, setEditingRevision] = useState(0);
  const [showAdvancedEdit, setShowAdvancedEdit] = useState(false);
  const [activeTab, setActiveTab] = useState('preview');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const autoSaveTimeoutRef = useRef(null);
  const autoClearTimeoutRef = useRef(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const REPORTS_PER_PAGE = 6; // Changed from 5 to 6 as per the user's request
  const [totalReports, setTotalReports] = useState(0);
  const [editingImage, setEditingImage] = useState(null);
  const [editingImageIndex, setEditingImageIndex] = useState(null);
  const [showChangelog, setShowChangelog] = useState(false);
  const [changelogContent, setChangelogContent] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showAdditionalFindings, setShowAdditionalFindings] = useState(false);
  const [shareableLink, setShareableLink] = useState('');
  const [viewModalReport, setViewModalReport] = useState(null);
  const [viewCounts, setViewCounts] = useState({});
  const [forceUpdate, setForceUpdate] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [existingReport, setExistingReport] = useState(null);
  const [shareClientModalReport, setShareClientModalReport] = useState(null);
  const [isUpdatingClients, setIsUpdatingClients] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [selectedInspectors, setSelectedInspectors] = useState([]);
  const [searchActiveTab, setSearchActiveTab] = useState('clients');
  const [unreadMessagesMap, setUnreadMessagesMap] = useState({});

  // Add this near the top of the file, after the imports
  const slideDownAnimation = `
    @keyframes slideDown {
      from {
        opacity: 0;
        transform: translateY(-10px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    .animate-slideDown {
      animation: slideDown 0.2s ease-out forwards;
    }
  `;

  // Add this in the component, before the return statement
  useEffect(() => {
    // Add the animation styles to the document
    const styleSheet = document.createElement("style");
    styleSheet.textContent = slideDownAnimation;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Update the auto-save effect
  useEffect(() => {
    // Clear any existing timeouts
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    if (autoClearTimeoutRef.current) {
      clearTimeout(autoClearTimeoutRef.current);
    }

    // Only set auto-save if we have substantial content, a user, and autosave is enabled
    if ((formData.clientName?.trim() || formData.propertyAddress?.trim()) && user?.uid && autosaveEnabled) {
      autoSaveTimeoutRef.current = setTimeout(async () => {
        try {
          // Check for existing drafts with the same address
          const draftsRef = collection(db, 'drafts');
          const draftsQuery = query(
            draftsRef,
            where('createdBy', '==', user.uid),
            where('propertyAddress', '==', formData.propertyAddress?.trim() || '')
          );
          const draftsSnapshot = await getDocs(draftsQuery);
          const existingDraft = draftsSnapshot.docs[0];  // Get first match if any

          const draftData = {
            ...formData,
            savedAt: new Date().toISOString(),
            createdBy: user.uid,
            clientName: formData.clientName || 'Untitled Draft'
          };

          let savedDraftId;
          if (existingDraft) {
            // Only update if content has changed
            const currentData = existingDraft.data();
            if (JSON.stringify(currentData) !== JSON.stringify(draftData)) {
              await updateDoc(doc(db, 'drafts', existingDraft.id), draftData);
              savedDraftId = existingDraft.id;
              setLastAutoSave(new Date());
              toast.success('Draft auto-saved', { duration: 2000 });
            }
          } else {
            // Create new draft
            const newDraftRef = await addDoc(collection(db, 'drafts'), draftData);
            savedDraftId = newDraftRef.id;
            setLastAutoSave(new Date());
            toast.success('Draft auto-saved', { duration: 2000 });
          }

          // Update local drafts state if we saved something
          if (savedDraftId) {
            setDrafts(prevDrafts => {
              const newDrafts = prevDrafts.filter(d => d.id !== savedDraftId);
              return [{
                id: savedDraftId,
                ...draftData
              }, ...newDrafts];
            });
          }
        } catch (error) {
          console.error('Auto-save failed:', error);
          // Only show error toast if it's not a permission error
          if (!error.message.includes('permission')) {
            toast.error('Failed to auto-save draft');
          }
        }
      }, 30000); // Auto-save after 30 seconds of inactivity
    }

    // Set auto-clear timeout if we only have notes
    if (!formData.clientName && !formData.propertyAddress && formData.customText) {
      autoClearTimeoutRef.current = setTimeout(() => {
        localStorage.removeItem('tankFormData');
        setFormData(prev => ({
          ...prev,
          customText: ''
        }));
        toast.info('Notes cleared due to inactivity', { duration: 3000 });
      }, 600000); // Auto-clear after 10 minutes
    }

    // Cleanup
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (autoClearTimeoutRef.current) {
        clearTimeout(autoClearTimeoutRef.current);
      }
    };
  }, [formData, user, autosaveEnabled]); // Add autosaveEnabled to dependencies

  // Add this function near the top of the component, after the state declarations
  const loadGoogleMapsScript = () => {
    if (window.google) return Promise.resolve();
    
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = `..meta.env.VITE_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.id = 'google-maps-script';
      script.async = true;
      script.defer = true;
      
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google Maps script'));
      
      document.head.appendChild(script);
    });
  };

  // Replace the existing Google Maps useEffect with this updated version
  useEffect(() => {
    let autocomplete = null;
    let listener = null;
    
    const initializeAutocomplete = async () => {
      try {
        await loadGoogleMapsScript();
        
        if (addressInputRef.current && window.google) {
          autocomplete = new window.google.maps.places.Autocomplete(addressInputRef.current, {
            types: ["address"],
            componentRestrictions: { country: "us" }
          });

          listener = autocomplete.addListener('place_changed', () => {
            const place = autocomplete.getPlace();
            if (place.formatted_address) {
              setFormData(prev => ({
                ...prev,
                propertyAddress: place.formatted_address
              }));
            }
          });
        }
      } catch (error) {
        console.error('Error initializing Google Maps:', error);
      }
    };

    initializeAutocomplete();

    // Cleanup function
    return () => {
      if (listener) {
        window.google?.maps.event.removeListener(listener);
      }
      if (autocomplete) {
        google.maps.event.clearInstanceListeners(autocomplete);
      }
      // Remove the script tag if component unmounts
      const script = document.getElementById('google-maps-script');
      if (script) {
        script.remove();
      }
    };
  }, []);

  // Update image preview useEffect with cleanup
  useEffect(() => {
    const savedPreview = localStorage.getItem('tankFormImagePreview');
    if (savedPreview) {
      setImagePreviews(prev => [...prev, savedPreview]);
    }
    
    // Cleanup function to revoke object URLs
    return () => {
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  // Add this helper function at the top level of your component
  const checkIfUserIsCollaborator = (collaborators, userId) => {
    if (!collaborators || !Array.isArray(collaborators) || collaborators.length === 0 || !userId) {
      return false;
    }
    
    return collaborators.some(collaborator => {
      // Handle when collaborator is an object with id field
      if (collaborator && typeof collaborator === 'object' && 'id' in collaborator) {
        return collaborator.id === userId;
      }
      
      // Handle when collaborator is a string (just the user ID)
      if (typeof collaborator === 'string') {
        return collaborator === userId;
      }
      
      return false;
    });
  };

  // Extract loadInitialData into a reusable function that can be called from multiple places
  const loadInitialData = useCallback(async () => {
    try {
      if (!user) return;
      
      console.log('loadInitialData called. User:', user.email);
      
      // Use firestore to query reports and drafts
      const db = getFirestore();
      
      // Clear any previously loaded reports/drafts
      setRecentReports([]);
      setDrafts([]);
      
      // Reference to reports collection
      const reportsRef = collection(db, 'inspections');
      
      // Create a query for reports created by the current user
      const createdByQuery = query(
        reportsRef, 
        where('createdBy', '==', user.uid), 
        orderBy('dateOfInspection', 'desc'),
        limit(REPORTS_PER_PAGE) // Add limit here to only get REPORTS_PER_PAGE initially
      );
      
      // Execute the query
      const querySnapshot = await getDocs(createdByQuery);
      
      // Map of reports to display, using a Map to avoid duplicates
      const reportsMap = new Map();
      
      // Add reports created by the user
      querySnapshot.docs.forEach(doc => {
        reportsMap.set(doc.id, {
          id: doc.id,
          ...doc.data()
        });
      });
      
      // For collaborators, we need to get all reports to check for collaboration
      const allReportsQuery = query(
        reportsRef,
        orderBy('dateOfInspection', 'desc')
      );
      
      // Get all reports (we'll filter client-side for collaborations)
      const allReportsSnapshot = await getDocs(allReportsQuery);
      
      // Count total reports including collaborations (for pagination info)
      let totalCount = 0;
      let collaboratorReports = [];
      
      // Add reports where user is collaborator (filtered client-side)
      allReportsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Count reports created by the user
        if (data.createdBy === user.uid) {
          totalCount++;
        }
        
        // Check if user is a collaborator using the helper function
        const isCollaborator = checkIfUserIsCollaborator(data.collaborators, user.uid);
        
        if (isCollaborator) {
          // Always add collaborator reports, even if we already have this report in reportsMap
          collaboratorReports.push({
            id: doc.id,
            ...data,
            isCollaborator: true // Add flag to indicate this is a collaborative report
          });
          
          // Only count for pagination if not already counted
          if (data.createdBy !== user.uid) {
            totalCount++;
          }
        }
      });
      
      // Sort collaborator reports by date of inspection (newest first)
      collaboratorReports.sort((a, b) => {
        const dateA = a.dateOfInspection ? new Date(a.dateOfInspection) : new Date(0);
        const dateB = b.dateOfInspection ? new Date(b.dateOfInspection) : new Date(0);
        return dateB - dateA;
      });
      
      // Show all collaborator reports, up to a reasonable limit combined with owned reports
      const maxReportsToShow = Math.max(REPORTS_PER_PAGE, reportsMap.size + collaboratorReports.length);
      
      // Add collaborator reports to the map
      collaboratorReports.forEach(report => {
        reportsMap.set(report.id, report);
      });
      
      // Convert to array and sort by date of inspection
      const reports = Array.from(reportsMap.values()).sort((a, b) => {
        const dateA = a.dateOfInspection ? new Date(a.dateOfInspection) : new Date(0);
        const dateB = b.dateOfInspection ? new Date(b.dateOfInspection) : new Date(0);
        return dateB - dateA;
      }).slice(0, maxReportsToShow);
      
      // Set reports and total count
      setRecentReports(reports);
      
      // Get total count of all reports (both created and collaborated)
      setTotalReports(totalCount);
      
      // Set hasMore based on whether there are more reports to load
      setHasMore(totalCount > reports.length);
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Error loading data');
    } finally {
      setIsLoading(false);
    }
  }, [user, REPORTS_PER_PAGE]);

  // Update the useEffect to use the extracted function
  useEffect(() => {
    let unsubscribe;
    
    // Only load data if we have a user and they're not in the process of logging in/out
    if (!loading && user) {
      loadInitialData();
    }

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, loading, loadInitialData]); // Add searchTerm to dependencies

  // Add new useEffect for handling search
  useEffect(() => {
    const searchTimeout = setTimeout(async () => {
      if (!user?.uid) return;
      
      if (searchTerm) {
        setIsSearching(true);
        try {
          const reportsRef = collection(db, 'inspections');
          const searchLower = searchTerm.toLowerCase();
          
          // Create compound query for search
          const searchQuery = query(
            reportsRef,
            where('createdBy', '==', user.uid),
            orderBy('dateOfInspection', 'desc')
          );
          
          const searchSnapshot = await getDocs(searchQuery);
          const searchResults = searchSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(report => {
              return (
                report.propertyAddress?.toLowerCase().includes(searchLower) ||
                report.clientName?.toLowerCase().includes(searchLower) ||
                formatLongDate(report.dateOfInspection).toLowerCase().includes(searchLower) ||
                formatShortDate(report.dateOfInspection).toLowerCase().includes(searchLower)
              );
            });
          
          setRecentReports(searchResults);
          setTotalReports(searchResults.length);
          setHasMore(false);
        } catch (error) {
          console.error('Error searching reports:', error);
          toast.error('Error searching reports');
        } finally {
          setIsSearching(false);
        }
      }
    }, 500); // Increased from 300ms to 500ms

    return () => clearTimeout(searchTimeout);
  }, [searchTerm, user?.uid]);

  // Add effect to save to localStorage whenever formData changes
  useEffect(() => {
    if (formData.clientName || formData.propertyAddress || formData.customText) {
      saveToLocalStorage(formData);
    }
  }, [formData]);

  // Utility functions
  const formatLongDate = (dateValue) => {
    if (!dateValue) return '';
    
    try {
      let d;
      
      // Handle Firebase Timestamp objects that might be serialized in different ways
      if (typeof dateValue === 'object' && dateValue.seconds && dateValue.nanoseconds) {
        // This is a Firebase Timestamp object
        d = new Date(dateValue.seconds * 1000);
      } else if (typeof dateValue === 'string' && dateValue.includes('at')) {
        // This handles the case when it's a string like "March 4, 2025 at 1:37:30 AM UTC-5"
        d = new Date(dateValue);
      } else {
        // Handle normal date strings
        d = new Date(dateValue);
      }
      
      // Check if date is invalid
      if (isNaN(d.getTime())) {
        console.warn('Invalid date encountered:', dateValue);
        return '';
      }
      
      const options = { year: 'numeric', month: 'long', day: 'numeric' };
      return d.toLocaleDateString('en-US', options);
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return '';
    }
  };

  // Modify the formatShortDate function to add a responsive option
  const formatShortDate = (dateValue, isMobile = false) => {
    if (!dateValue) return '';
    
    try {
      let d;
      
      // Handle Firebase Timestamp objects that might be serialized in different ways
      if (typeof dateValue === 'object' && dateValue.seconds && dateValue.nanoseconds) {
        // This is a Firebase Timestamp object
        d = new Date(dateValue.seconds * 1000);
      } else if (typeof dateValue === 'string' && dateValue.includes('at')) {
        // This handles the case when it's a string like "March 4, 2025 at 1:37:30 AM UTC-5"
        d = new Date(dateValue);
      } else {
        // Handle normal date strings
        d = new Date(dateValue);
      }
      
      // Check if date is invalid
      if (isNaN(d.getTime())) {
        console.warn('Invalid date encountered:', dateValue);
        return '';
      }
      
      let mm = String(d.getMonth() + 1).padStart(2, '0');
      let dd = String(d.getDate()).padStart(2, '0');
      let yy = String(d.getFullYear()).slice(-2);
      
      // Return shorter format for mobile
      return isMobile ? `${mm}/${dd}` : `${mm}/${dd}/${yy}`;
    } catch (error) {
      console.error('Error formatting date:', error, dateValue);
      return '';
    }
  };

  const removeUSA = (addr) => {
    if (!addr) return '';
    return addr
      .replace(/,\s*USA$/i, '')
      .replace(/,\s*[A-Z]{2}\s*$/, '')
      .replace(/,\s*United States$/i, '')
      .replace(/,\s*New Jersey$/i, '')
      .replace(/,\s*NJ$/i, '');
  };

  // Add this function to upload an image to Firebase Storage
  const uploadImageToStorage = async (file, index) => {
    try {
      const timestamp = Date.now();
      const fileName = `${timestamp}-${file.name}`;
      const storageRef = ref(storage, `images/${fileName}`);
      
      // Upload the file. Owner/report metadata lets the Storage rules limit edits to the report's own inspectors.
      const uploadTask = uploadBytesResumable(storageRef, file, { customMetadata: { owner: user?.uid || '', ...(editingReportId ? { reportId: editingReportId } : {}) } });
      
      // Return a promise that resolves with the download URL
      return new Promise((resolve, reject) => {
        uploadTask.on('state_changed',
          (snapshot) => {
            // Progress tracking
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            setImageUploadProgress(prev => ({
              ...prev,
              [fileName]: Math.round(progress)
            }));
          },
          (error) => {
            console.error('Upload error:', error);
            setImageUploadProgress(prev => ({
              ...prev,
              [fileName]: 'error'
            }));
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              setImageUploadProgress(prev => ({
                ...prev,
                [fileName]: 'complete'
              }));
              resolve(downloadURL);
            } catch (error) {
              console.error('Error getting download URL:', error);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error('Error in uploadImageToStorage:', error);
      throw error;
    }
  };

  // Add this function to delete an image from Firebase Storage
  const deleteImageFromStorage = async (imagePath) => {
    try {
      if (!imagePath) return;
      
      let path;
      
      // Helper function to extract path from Firebase Storage URL
      const extractPathFromUrl = (url) => {
        try {
          // Remove the base URL and query parameters
          const urlWithoutQuery = url.split('?')[0];
          // Get everything after /o/
          const pathPart = urlWithoutQuery.split('/o/')[1];
          if (!pathPart) throw new Error('Invalid storage URL format');
          // Decode the URL-encoded path
          return decodeURIComponent(pathPart);
        } catch (error) {
          console.error('Error extracting path from URL:', error);
          return null;
        }
      };

      // Handle different input formats
      if (typeof imagePath === 'string') {
        if (imagePath.includes('firebasestorage.googleapis.com')) {
          path = extractPathFromUrl(imagePath);
        } else if (imagePath.startsWith('images/')) {
          path = imagePath;
        } else {
          console.error('Invalid image path format:', imagePath);
          return;
        }
      } else if (imagePath.url && imagePath.url.includes('firebasestorage.googleapis.com')) {
        path = extractPathFromUrl(imagePath.url);
      } else if (imagePath.path && imagePath.path.startsWith('images/')) {
        path = imagePath.path;
      } else {
        console.error('Invalid image data format:', imagePath);
        return;
      }

      if (!path) {
        console.error('Could not determine storage path for:', imagePath);
        return;
      }

      console.log('🗑️ Attempting to delete image:', path);
      const imageRef = ref(storage, path);
      
      try {
        await deleteObject(imageRef);
        console.log('✅ Image deleted successfully:', path);
      } catch (deleteError) {
        if (deleteError.code === 'storage/object-not-found') {
          console.log('⚠️ Image already deleted or does not exist:', path);
          return;
        }
        console.error('❌ Error deleting from storage:', deleteError);
        throw deleteError;
      }
    } catch (error) {
      console.error('❌ Error in deleteImageFromStorage:', error);
      // Don't throw the error, just log it and continue
    }
  };

  // Update the handleImageUpload function
  const handleImageUpload = async (files) => {
    if (!files.length) return;
    
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const validImageTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/heic', 'image/heif'];
    const toastId = toast.loading('Processing images...');
    const uploadedUrls = [];

    try {
      const processedFiles = [];
      const processedPreviews = [];

      for (const file of files) {
        try {
          if (file.size > MAX_FILE_SIZE) {
            toast.error(`${file.name} is too large. Please choose images under 10MB.`);
            continue;
          }

          let imageFile = file;
          const isHeic = file.name.toLowerCase().endsWith('.heic') || 
                        file.name.toLowerCase().endsWith('.heif') ||
                        file.type.toLowerCase() === 'image/heic' || 
                        file.type.toLowerCase() === 'image/heif';

          if (isHeic) {
            console.log('Converting HEIC file:', file.name, 'Type:', file.type, 'Size:', file.size);
            try {
              const url = URL.createObjectURL(file);
              const img = new Image();
              
              const convertViaCanvas = () => new Promise((resolve, reject) => {
                img.onload = () => {
                  const canvas = document.createElement('canvas');
                  canvas.width = img.width;
                  canvas.height = img.height;
                  const ctx = canvas.getContext('2d');
                  ctx.drawImage(img, 0, 0);
                  
                  canvas.toBlob(
                    blob => {
                      if (blob) {
                        resolve(blob);
                      } else {
                        reject(new Error('Canvas conversion failed'));
                      }
                    },
                    'image/jpeg',
                    0.8
                  );
                };
                img.onerror = () => reject(new Error('Image loading failed'));
              });

              img.src = url;
              const blob = await convertViaCanvas();
              imageFile = new File([blob], file.name.replace(/\.(heic|heif)$/i, '.jpg'), {
                type: 'image/jpeg'
              });
              
              URL.revokeObjectURL(url);
              console.log('HEIC conversion successful');
            } catch (error) {
              console.error('HEIC conversion failed:', error);
              toast.error(`Could not convert ${file.name}. Please convert to JPEG first using the Photos app.`);
              continue;
            }
          } else if (!validImageTypes.includes(file.type.toLowerCase())) {
            toast.error(`${file.name} is not a valid image type.`);
            continue;
          }

          // Compression options
          const options = {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 1920,
            useWebWorker: true,
            fileType: 'image/jpeg',
            quality: 0.8
          };

          const compressedFile = await imageCompression(imageFile, options);
          const previewUrl = URL.createObjectURL(compressedFile);
          
          // Upload to Firebase Storage
          const downloadURL = await uploadImageToStorage(compressedFile, processedFiles.length);
          uploadedUrls.push({ url: downloadURL, path: `images/${Date.now()}-${file.name}` });
          
          processedFiles.push(compressedFile);
          processedPreviews.push(previewUrl);
          
          toast.success(`Processed ${file.name} successfully!`, {
            duration: 2000
          });
          
        } catch (fileError) {
          console.error('Error processing file:', file.name, fileError);
          toast.error(`Error processing ${file.name}. Please try again.`);
          continue;
        }
      }

      if (processedFiles.length > 0) {
        setImageFiles(prev => [...prev, ...processedFiles]);
        setImagePreviews(prev => [...prev, ...processedPreviews]);
        
        // Update formData with the new image URLs
        setFormData(prev => ({
          ...prev,
          imageUrls: [...(prev.imageUrls || []), ...uploadedUrls]
        }));
        
        toast.success(`${processedFiles.length} image(s) processed successfully!`, { id: toastId });
      } else {
        toast.error('No images were processed successfully.', { id: toastId });
      }
    } catch (error) {
      console.error('Error in image upload:', error);
      toast.error('Error processing images. Please try again.', { id: toastId });
    }
  };

  const handleFileInputChange = (e) => {
    handleImageUpload(Array.from(e.target.files));
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const droppedFiles = Array.from(e.dataTransfer.files).filter(file => {
      const isImage = file.type.startsWith('image/');
      if (!isImage) {
        toast.error(`${file.name} is not an image file.`);
      }
      return isImage;
    });
    
    if (droppedFiles.length > 0) {
      handleImageUpload(droppedFiles);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('bg-blue-50');
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('bg-blue-50');
  };

  // Update the removeImage function
  const removeImage = (index) => {
    // Update form data to remove the image URL
    setFormData(prev => ({
      ...prev,
      imageUrls: prev.imageUrls.filter((_, i) => i !== index)
    }));

    // Update local state
    setImageFiles(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => {
      const newPreviews = prev.filter((_, i) => i !== index);
      // Revoke old preview URL
      URL.revokeObjectURL(prev[index]);
      return newPreviews;
    });

    // Log to console instead of showing toast
    console.log('Image removed from form');
  };

  // Update the clearAllImages function
  const clearAllImages = () => {
    // Clear form data image URLs
    setFormData(prev => ({
      ...prev,
      imageUrls: []
    }));

    // Clear local state
    imagePreviews.forEach(url => URL.revokeObjectURL(url));
    setImageFiles([]);
    setImagePreviews([]);

    // Log to console instead of showing toast
    console.log('All images cleared from form');
  };

  // Report title by inspection type and scope (for share/download and display)
  const getReportTitle = () => {
    if (formData.inspectionType === 'standard') return 'Oil Tank Sweep Report';
    if (formData.inspectionType === 'gpr' && formData.gprScope === 'targeted') return 'Oil Tank Sweep Report with Targeted Ground-Penetrating Radar Evaluation';
    if (formData.inspectionType === 'gpr') return 'Oil Tank Sweep Report with Ground-Penetrating Radar Imaging';
    return 'Oil Tank Sweep Report';
  };

  // ============================================================================
  // generateBodyText: inspection type, GPR scope, equipment, conclusions, limitations
  // ============================================================================
  const generateBodyText = () => {
    let baseText = "";
    const location = formData.anomalyLocation === "custom" ? formData.customLocation.trim() : formData.anomalyLocation;
    const isGpr = formData.inspectionType === "gpr";
    const isTargeted = formData.gprScope === "targeted";

    // ---------- Intro paragraph by scenario ----------
    if (formData.scenario === "noTank") {
      baseText = `Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). We did not detect any anomalies with our metal detection equipment that match the size of a UST.`;
    } else if (formData.scenario === "anomaly") {
      baseText = `Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). A metallic anomaly, similar in size to an underground storage tank (UST), was detected${location ? " " + location : ""}.`;
    } else if (formData.scenario === "ustFound") {
      baseText = `Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). An underground storage tank (UST) was identified${location ? " " + location : ""}.`;
    } else if (formData.scenario === "dataInconclusive") {
      baseText = `Smart Choice Inspections LLC conducted a visual inspection and physical sweep of the referenced property to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). Site conditions prevented a definitive conclusion regarding the presence of an underground storage tank (UST).`;
    }

    // ---------- Scenario-specific conclusion (before equipment); for GPR full no UST, additional notes go before "While non-invasive..." ----------
    if (formData.scenario === "noTank") {
      if (!isGpr) {
        if (formData.customText?.trim()) {
          baseText += " " + formData.customText.trim();
          if (!formData.customText.trim().endsWith(".")) baseText += ".";
        }
        if (formData.limitations?.length > 0) baseText += " " + generateLimitationsText();
        baseText += " This does not guarantee the absence of USTs on the property; but it is unlikely.";
      } else if (isTargeted) {
        if (formData.customText?.trim()) {
          baseText += " " + formData.customText.trim();
          if (!formData.customText.trim().endsWith(".")) baseText += ".";
        }
        if (formData.limitations?.length > 0) baseText += " " + generateLimitationsText();
        baseText += " The evaluated anomaly did not exhibit radar characteristics consistent with an underground storage tank. This conclusion applies only to the defined evaluation area.";
      } else {
        // GPR full no UST: finding first, then additional notes & limitations, then disclaimer
        baseText += " No metallic responses or hyperbolic radar reflections consistent with the size and cross-sectional shape of an underground storage tank were observed in the scanned areas.";
        if (formData.customText?.trim()) {
          baseText += " " + formData.customText.trim();
          if (!formData.customText.trim().endsWith(".")) baseText += ".";
        }
        if (formData.limitations?.length > 0) baseText += " " + generateLimitationsText();
        baseText += " While non-invasive detection methods are effective, no method can guarantee the complete absence of buried objects.";
      }
    } else if (formData.scenario === "anomaly") {
      if (formData.customText?.trim()) {
        baseText += " " + formData.customText.trim();
        if (!formData.customText.trim().endsWith(".")) baseText += ".";
      }
      if (formData.limitations?.length > 0) baseText += " " + generateLimitationsText();
      baseText += " We recommend requesting OPRA records for any information on previously decommissioned oil tanks. If no records are found, proceed with an intrusive investigation.";
    } else if (formData.scenario === "ustFound") {
      if (formData.customText?.trim()) {
        baseText += " " + formData.customText.trim();
        if (!formData.customText.trim().endsWith(".")) baseText += ".";
      }
      if (formData.limitations?.length > 0) baseText += " " + generateLimitationsText();
      if (isGpr && isTargeted) {
        baseText += " A distinct hyperbolic reflection consistent with the size and cross-sectional shape of an underground storage tank was observed within the defined evaluation area. Further intrusive verification is recommended.";
      } else {
        baseText += " This UST may pose a risk of contamination if not addressed. We strongly recommend the removal or proper decommissioning of this UST, along with soil testing for potential contamination.";
      }
    } else if (formData.scenario === "dataInconclusive") {
      if (formData.customText?.trim()) {
        baseText += " " + formData.customText.trim();
        if (!formData.customText.trim().endsWith(".")) baseText += ".";
      }
      if (formData.limitations?.length > 0) baseText += " " + generateLimitationsText();
      baseText += " Follow-up evaluation may be necessary.";
    }

    // ---------- Equipment (metal always; GPR optional) ----------
    const getGprTargetedReasonText = () => {
      const r = formData.gprTargetedReason;
      if (r === 'metal_detection') return 'a metal detection anomaly';
      if (r === 'visual') return 'a visual indicator (fill or vent pipe)';
      if (r === 'prior_docs') return 'prior documentation';
      if (r === 'client_request') return 'client request';
      if (r === 'other') return formData.gprTargetedReasonOther?.trim() || 'other';
      return '';
    };
    const loc = formData.gprTargetedLocation?.trim() || '';
    let equipmentBlock;
    if (isTargeted && loc) {
      const reasonText = getGprTargetedReasonText();
      const reasonPhrase = reasonText ? ", due to " + reasonText + "," : "";
      equipmentBlock = "\n\nUtilizing electronic metal detection equipment (Fisher TW-6) and a magnetic locating device (Schonstedt 92XTD), the defined area of concern located at " + loc + " was evaluated for buried metallic objects consistent with underground storage tanks. Ground-penetrating radar (Easy Radar ER500A) was also utilized to evaluate that same area" + reasonPhrase + " to assess subsurface conditions. This was a targeted evaluation only; the full property was not swept and this does not represent a full-property subsurface scan.";
    } else {
      const metalPara = "Utilizing electronic metal detection equipment (Fisher TW-6) and a magnetic locating device (Schonstedt 92XTD), accessible yard areas were swept for buried metallic objects consistent with underground storage tanks. The detection equipment used can detect metallic objects up to a depth of six feet. The property was swept to a distance of at least 30ft around the perimeter of the house.";
      equipmentBlock = "\n\n" + metalPara;
      if (isGpr) {
        equipmentBlock += "\n\nIn addition, ground-penetrating radar (Easy Radar ER500A) was utilized to evaluate subsurface conditions and identify hyperbolic reflections consistent with underground storage tanks.";
      }
    }

    baseText += equipmentBlock;

    // ---------- Middle sentence (standard noTank only) ----------
    if (!isGpr && formData.scenario === "noTank") {
      baseText += "\n\nNo other metal anomalies consistent with the size of an underground storage tank were identified during the oil tank sweep.";
    }

    // ---------- Closing and Terms ----------
    baseText += "\n\nIf you have any questions about this report please feel free to contact us.\n\nTerms and Conditions: This report excludes areas where access to the magnetic locator was restricted due to obstructions such as dense vegetation, denied access, or structural interference. The methods used at this site have been used successfully at many sites to locate underground storage tanks and drums. There can be no guarantee, however, that every target will be detected at a particular site. Subsurface conditions (e.g. rebar/wire mesh under concrete, foundations, structures and others) may prevent some or all geophysical locating methods from working properly.";

    if (isGpr) {
      baseText += " Ground-penetrating radar performance may be affected by soil composition, moisture content, reinforced concrete, buried utilities, subsurface debris, or signal attenuation. Radar reflections represent geophysical responses and may require intrusive confirmation.";
    }
    if (isGpr && isTargeted) {
      baseText += " This evaluation was limited to the specific area identified above. Areas outside that defined zone were not assessed using radar imaging.";
    }

    return baseText;
  };

  // Helper: Fit image onto a PDF page
  const fitImageToPage = (imgWidth, imgHeight, maxWidth, maxHeight) => {
    const ratio = Math.min(maxWidth / imgWidth, maxHeight / imgHeight);
    return {
      width: imgWidth * ratio,
      height: imgHeight * ratio
    };
  };

  // Add this validation function
  const validateFormData = () => {
    const errors = [];
    
    // Client name validation
    if (!formData.clientName?.trim()) {
      errors.push('Client name is required');
    } else if (formData.clientName.trim().length < 2) {
      errors.push('Client name must be at least 2 characters long');
    }
    
    // Address validation
    if (!formData.propertyAddress?.trim()) {
      errors.push('Property address is required');
    } else if (!formData.propertyAddress.includes(',')) {
      errors.push('Please enter a complete address with city and state');
    }
    
    // Date validation
    if (!formData.dateOfInspection) {
      errors.push('Date of inspection is required');
    } else {
      const inspectionDate = new Date(formData.dateOfInspection);
      const today = new Date();
      
      // Set both dates to start of day for accurate comparison
      inspectionDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);
      
      if (inspectionDate > today) {
        errors.push('Inspection date cannot be in the future');
      }
      
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
      sixMonthsAgo.setHours(0, 0, 0, 0);
      if (inspectionDate < sixMonthsAgo) {
        errors.push('Inspection date cannot be more than 6 months old');
      }
    }
    
    // Scenario validation (location required for anomaly/ustFound, not for dataInconclusive)
    if (formData.scenario === 'anomaly' || formData.scenario === 'ustFound') {
      if (!formData.anomalyLocation) {
        errors.push('Location is required when an anomaly or UST is detected');
      }
      if (formData.anomalyLocation === 'custom' && !formData.customLocation?.trim()) {
        errors.push('Custom location description is required');
      }
    }

    // GPR targeted scope validation
    if (formData.inspectionType === 'gpr' && formData.gprScope === 'targeted') {
      if (!formData.gprTargetedLocation?.trim()) {
        errors.push('Location of targeted area is required for targeted GPR evaluation');
      }
      if (!formData.gprTargetedReason) {
        errors.push('Reason for targeted evaluation is required');
      }
      if (formData.gprTargetedReason === 'other' && !formData.gprTargetedReasonOther?.trim()) {
        errors.push('Please specify the reason for targeted evaluation');
      }
    }

    // Validate limitations if any are present but incomplete
    if (formData.limitations?.length > 0) {
      const incompleteLimitations = formData.limitations.some(
        limitation => !limitation.type || !limitation.location
      );
      if (incompleteLimitations) {
        errors.push('Please complete or remove any partial limitations');
      }
    }
    
    return errors;
  };

  // Update the generateReport function to ensure proper form clearing
  const generateReport = async () => {
    let reportId = editingReportId;
    // Define toastId outside try/catch so it's accessible in catch block
    let toastId;

    try {
      setIsSubmitting(true);
      toastId = toast.loading(editingReportId ? 'Updating report...' : 'Generating report...');

      // Validate form data
      const validationErrors = validateFormData();
      if (validationErrors.length > 0) {
        toast.error(
          <div>
            <strong>Please correct the following:</strong>
            <ul className="list-disc pl-4 mt-2">
              {validationErrors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </div>,
          { duration: 5000, id: toastId }
        );
        setIsSubmitting(false);
        return;
      }

      console.log('Current editingReportId:', editingReportId); // Debug log
      
      // Store image URLs before clearing anything
      const imageUrlsToEmbed = [...formData.imageUrls];

      // Prepare the data to save
      console.log('Current user:', user); // Debug log
      console.log('User ID:', user?.uid); // Debug log
      
      // Check if this is a collaborative update
      const isCollaborativeUpdate = editingReportId && existingReport && existingReport.createdBy !== user.uid;
      
      const reportData = {
        clientName: formData.clientName,
        propertyAddress: formData.propertyAddress,
        dateOfInspection: formData.dateOfInspection,
        inspectionType: formData.inspectionType || 'standard',
        gprScope: formData.gprScope || 'full',
        gprTargetedLocation: formData.gprTargetedLocation || '',
        gprTargetedReason: formData.gprTargetedReason || '',
        gprTargetedReasonOther: formData.gprTargetedReasonOther || '',
        fullDocumentText: formData.fullDocumentText || '',
        customText: formData.customText || '',
        customLocation: formData.customLocation || '',
        anomalyLocation: formData.anomalyLocation || '',
        scenario: formData.scenario || 'noTank',
        limitations: formData.limitations || [],
        // For existing reports being edited by collaborators, keep the original createdBy
        createdBy: isCollaborativeUpdate ? existingReport.createdBy : user.uid,
        createdAt: isCollaborativeUpdate ? existingReport.createdAt : new Date().toISOString(),
        appVersion: APP_VERSION,
        viewCount: editingReportId ? (existingReport?.viewCount || 0) : 0,
        downloadCount: editingReportId ? (existingReport?.downloadCount || 0) : 0,
        viewLog: editingReportId ? (existingReport?.viewLog || []) : [],
        downloadLog: editingReportId ? (existingReport?.downloadLog || []) : [],
        findings: formData.findings || {
          vent: false,
          fill: false,
          distribution: false,
        },
        imageUrls: formData.imageUrls || [],
        updatedAt: new Date().toISOString(),
        updatedBy: user.uid, // Add this field to track who made the last update
        lastModifiedBy: user.displayName || user.email, // Add a readable name for who last modified it
        isPaid: defaultPaymentStatus, // Use the user preference setting
        resources: formData.resources, // Add resources to the report data
        portalClients: selectedClients.length > 0 
          ? selectedClients.map(client => ({
              id: client.id,
              firstName: client.firstName,
              lastName: client.lastName,
              email: client.email
            }))
          : (existingReport?.portalClients || []), // Use existing portal clients if none selected
        collaborators: selectedInspectors.length > 0
          ? selectedInspectors.map(inspector => ({
              id: inspector.id,
              displayName: inspector.displayName || `${inspector.firstName || ''} ${inspector.lastName || ''}`.trim(),
              email: inspector.email
            }))
          : (existingReport?.collaborators || []), // Use existing collaborators if none selected
      };
      // Flat id list for the security rules (collaborator maps cannot be matched in rules)
      reportData.collaboratorIds = (reportData.collaborators || []).map(c => (typeof c === 'string' ? c : c?.id)).filter(Boolean);
      
      console.log('Report data being saved:', reportData); // Debug log
      
      if (editingReportId) {
        // Update existing report
        console.log('Updating existing report:', editingReportId);
        const reportRef = doc(db, 'inspections', editingReportId);
        let savedRevision = editingRevision;
        try {
          await runTransaction(db, async (tx) => {
            const snap = await tx.get(reportRef);
            if (!snap.exists()) throw new Error('REPORT_MISSING');
            const currentRevision = snap.data().revision || 0;
            if (currentRevision !== (editingRevision || 0)) throw new Error('REPORT_CHANGED');
            savedRevision = currentRevision + 1;
            tx.update(reportRef, { ...reportData, revision: savedRevision });
          });
        } catch (txError) {
          if (txError.message === 'REPORT_CHANGED') {
            toast.error('This report was changed elsewhere (another inspector or Horizon) since you opened it. Reload it from the list and apply your changes again.', { id: toastId, duration: 8000 });
            setIsSubmitting(false);
            return;
          }
          if (txError.message === 'REPORT_MISSING') {
            toast.error('This report no longer exists.', { id: toastId });
            setIsSubmitting(false);
            return;
          }
          throw txError;
        }
        setEditingRevision(savedRevision);
        reportId = editingReportId;
        toast.success('Report updated successfully!', { id: toastId });
      } else {
        // Create new report
        console.log('Creating new report');
        reportData.createdAt = new Date().toISOString();
        reportData.revision = 1;
        const docRef = await addDoc(collection(db, 'inspections'), reportData);
        reportId = docRef.id;
        // Tag the photos with the new report id so collaborators may edit them later (best effort).
        for (const image of reportData.imageUrls || []) {
          try {
            const match = /\/o\/([^?]+)/.exec(image.url || '');
            if (match) await updateMetadata(ref(storage, decodeURIComponent(match[1])), { customMetadata: { owner: user?.uid || '', reportId } });
          } catch (metaError) { console.warn('Could not tag photo with report id', metaError); }
        }
        toast.success('Report generated successfully!', { id: toastId });
      }

      // Only generate PDF if autoPdfEnabled is true
      if (autoPdfEnabled) {
        // Generate PDF...
        // ... existing PDF generation code ...
      }
      
      // Add the report ID to each selected client's savedReports array in the portal database
      if (selectedClients.length > 0 && reportId) {
        try {
          // Import what we need for the portal database
          const { portalDb } = await import('../utils/portalFirebase');
          
          // Update each client's savedReports array and notify them
          const updatePromises = selectedClients.map(async client => { 
            // Update the savedReports array
            await updateDoc(doc(portalDb, 'users', client.id), {
              savedReports: arrayUnion(reportId)
            });
            
            // Send notification to each client
            const notificationResult = await notifyReportShared(reportId, client.email, db, portalDb);
            if (notificationResult.success) {
              console.log(`Notification sent to client: ${client.email}`);
            } else {
              console.error(`Failed to notify client: ${client.email}`, notificationResult.error);
            }
          });
          
          await Promise.all(updatePromises);
          console.log('Successfully shared report with portal clients');
        } catch (error) {
          console.error('Error sharing report with portal clients:', error);
        }
      }
      
      // Clear form data and localStorage after successful report generation
      localStorage.removeItem('tankFormData');
      
      // Delete any existing draft for this property address
      try {
        const draftsRef = collection(db, 'drafts');
        const draftsQuery = query(
          draftsRef,
          where('createdBy', '==', user.uid),
          where('propertyAddress', '==', formData.propertyAddress?.trim() || '')
        );
        const draftsSnapshot = await getDocs(draftsQuery);
        
        // Delete all matching drafts
        const batch = writeBatch(db);
        draftsSnapshot.docs.forEach(doc => {
          batch.delete(doc.ref);
        });
        await batch.commit();
        
        // Update local drafts state
        setDrafts(prev => prev.filter(draft => 
          !draftsSnapshot.docs.some(doc => doc.id === draft.id)
        ));
      } catch (error) {
        console.error('Error cleaning up drafts:', error);
        // Don't throw error, just log it - we still want to continue with form clearing
      }

      // Navigate to share report page if PDF was not generated
      if (!autoPdfEnabled) {
        navigate(`/share-report/${reportId}`);
      }
      
      // Clear form data
      setFormData({
        clientName: '',
        dateOfInspection: new Date().toISOString().split('T')[0],
        propertyAddress: '',
        inspectionType: 'standard',
        gprScope: 'full',
        gprTargetedLocation: '',
        gprTargetedReason: '',
        gprTargetedReasonOther: '',
        scenario: 'noTank',
        anomalyLocation: '',
        customLocation: '',
        customText: '',
        limitations: [],
        findings: {
          vent: false,
          fill: false,
          distribution: false
        },
        fullDocumentText: '',
        imageUrls: [], // Clear image URLs
        resources: [], // Reset resources
      });
      
      // Clear selectedClients state
      setSelectedClients([]);
      
      // Clear selectedInspectors state
      setSelectedInspectors([]);
      
      // Clear all images
      clearAllImages();
      
      // Clear editing state if we were editing
      if (editingReportId) {
        setEditingReportId(null);
        setExistingReport(null); // Also clear existingReport data
        console.log('Cleared editing state');
      }
      
      // Refresh the reports list to show the newly created/updated report
      // This ensures the app shows updated data without requiring a page refresh
      await loadInitialData();
      
    } catch (error) {
      console.error('Error in generateReport:', error);
      toast.error(editingReportId ? 'Error updating report' : 'Error generating report', { id: toastId });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper function to convert string date to Date object
  const parseDate = (dateString) => {
    if (!dateString) return new Date();
    return new Date(dateString);
  };

  // Add these new constants after the state declarations
  const limitationTypes = [
    { value: 'rebar', label: 'rebar' },
    { value: 'no access', label: 'no access' },
    { value: 'vehicles', label: 'vehicles' },
    { value: 'trailers', label: 'trailers' },
    { value: 'deck', label: 'deck' },
    { value: 'debris/clutter', label: 'debris/clutter' },
    { value: 'dense vegetation', label: 'dense vegetation' },
    { value: 'poor access', label: 'poor access' },
    { value: 'structural blockages', label: 'structural blockages' },
    { value: 'concrete slabs', label: 'concrete slabs' },
    { value: 'foundation areas', label: 'foundation areas' }
  ];

  const locationOptions = [
    { value: 'in the driveway', label: 'in the driveway' },
    { value: 'crawlspace', label: 'under crawlspace' },
    { value: 'in the backyard', label: 'in the backyard' },
    { value: 'in the front yard', label: 'in the front yard' },
    { value: 'under the deck', label: 'under the deck' },
    { value: 'under the patio', label: 'under the patio' },
    { value: 'in the rear stairs', label: 'in the rear stairs' },
    { value: 'by the sidewalk', label: 'by the sidewalk' },
    { value: 'throughout the property', label: 'throughout the property' }
  ];

  // Add these new functions after the existing utility functions
  const addLimitation = (type, location) => {
    const newLimitation = {
      id: Date.now(), // Unique ID
      type,
      location
    };
    
    setFormData(prev => ({
      ...prev,
      limitations: [...(prev.limitations || []), newLimitation]
    }));
  };

  const removeLimitation = (id) => {
    setFormData(prev => ({
      ...prev,
      limitations: prev.limitations.filter(l => l.id !== id)
    }));
  };

  const updateLimitation = (id, field, value) => {
    setFormData({
      ...formData,
      limitations: formData.limitations.map(limitation =>
        limitation.id === id ? { ...limitation, [field]: value } : limitation
      )
    });
  };

  const generateLimitationsText = () => {
    if (formData.limitations.length === 0) return '';

    const limitationStrings = formData.limitations
      .filter(limitation => {
        // Check if we have valid values for both type and location
        if (limitation.type === 'custom') {
          return limitation.customType?.trim() && limitation.location;
        } else if (limitation.location === 'custom') {
          return limitation.type && limitation.customLocation?.trim();
        }
        return limitation.type && limitation.location;
      })
      .map(limitation => {
        // Use the custom value if available; otherwise, use the predefined label
        const type = limitation.type === 'custom'
          ? limitation.customType?.trim()
          : limitationTypes.find(t => t.value === limitation.type)?.label || '';

        let location = '';
        if (limitation.location === 'custom') {
          location = limitation.customLocation?.trim();
        } else if (limitation.location === 'sidewalk') {
          location = 'by the sidewalk';
        } else if (limitation.location === 'throughout') {
          location = 'throughout the property';
        } else {
          location = locationOptions.find(l => l.value === limitation.location)?.label || '';
        }

        // Convert to lowercase and remove any trailing periods
        location = location.toLowerCase().replace(/\.$/, '');

        // Format without "in the" for special cases
        if (limitation.location === 'sidewalk' || limitation.location === 'throughout') {
          return `${type.toLowerCase()} ${location}`;
        }

        // For other locations: "debris/clutter in the backyard"
        return `${type.toLowerCase()} ${location}`;
      });

    if (limitationStrings.length === 0) return '';

    if (limitationStrings.length === 1) {
      return `However, ${limitationStrings[0]} obstructed our ability to sweep this area.`;
    }

    const lastLimitation = limitationStrings.pop();
    return `However, ${limitationStrings.join(', ')}, and ${lastLimitation} obstructed our ability to sweep these areas.`;
  };

  // Update getPrecedingText to use shortened version
  const getPrecedingText = () => {
    let baseText = "";
    const location = formData.anomalyLocation === 'custom' ? formData.customLocation.trim() : formData.anomalyLocation;
    
    if (formData.scenario === 'noTank') {
      baseText = `...to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). We did not detect any anomalies with our metal detection equipment that match the size of a UST.`;
    } else if (formData.scenario === 'anomaly') {
      baseText = `...storage tank (UST). A metallic anomaly, similar in size to an underground storage tank (UST), was detected${location ? " " + location : ""}. We recommend requesting OPRA records for any information on previously decommissioned oil tanks. If no records are found, proceed with an intrusive investigation.`;
    } else if (formData.scenario === 'ustFound') {
      baseText = `...storage tank (UST). An underground storage tank (UST) was identified${location ? " " + location : ""}. This UST may pose a risk of contamination if not addressed. We strongly recommend the removal or proper decommissioning of this UST, along with soil testing for potential contamination.`;
    }

    // Add limitations if present
    const limitations = generateLimitationsText();
    if (limitations) {
      baseText += ` ${limitations}`;
    }

    return baseText;
  };

  // Add save draft function
  const saveDraft = async () => {
    try {
      const draftData = {
        ...formData,
        savedAt: serverTimestamp(),
        createdBy: user.uid,
        clientName: formData.clientName || 'Untitled Draft'
      };

      // Check for existing drafts with the same address
      const draftsRef = collection(db, 'drafts');
      const draftsQuery = query(
        draftsRef,
        where('createdBy', '==', user.uid),
        where('propertyAddress', '==', formData.propertyAddress?.trim() || '')
      );
      const draftsSnapshot = await getDocs(draftsQuery);
      const existingDraft = draftsSnapshot.docs[0];

      let savedDraftId;
      if (existingDraft) {
        // Update existing draft
        await updateDoc(doc(db, 'drafts', existingDraft.id), draftData);
        savedDraftId = existingDraft.id;
      } else {
        // Create new draft
        const newDraftRef = await addDoc(collection(db, 'drafts'), draftData);
        savedDraftId = newDraftRef.id;
      }

      // Update local drafts state
      setDrafts(prevDrafts => {
        const newDrafts = prevDrafts.filter(d => d.id !== savedDraftId);
        return [{
          id: savedDraftId,
          ...draftData
        }, ...newDrafts];
      });

      toast.success('Draft saved successfully');
    } catch (error) {
      console.error('Error saving draft:', error);
      toast.error('Error saving draft');
    }
  };

  // Update the loadReport function to handle image loading better
  const loadReport = async (report, isEditing = false) => {
    setIsLoadingReport(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });

    try {
      await new Promise(resolve => setTimeout(resolve, 500));
      
      console.log('Loading report/draft with data:', report); // Log the report data
      
      // Store the entire report data for reference when updating
      if (isEditing) {
        setExistingReport(report);
        setEditingReportId(report.id); // Set the editing report ID to enable edit mode
        setEditingRevision(report.revision || 0);
      }
      
      const formattedData = {
        clientName: report.clientName || '',
        dateOfInspection: report.dateOfInspection || new Date().toISOString().split('T')[0],
        propertyAddress: report.propertyAddress || '',
        inspectionType: report.inspectionType || 'standard',
        gprScope: report.gprScope || 'full',
        gprTargetedLocation: report.gprTargetedLocation || '',
        gprTargetedReason: report.gprTargetedReason || '',
        gprTargetedReasonOther: report.gprTargetedReasonOther || '',
        scenario: report.scenario || 'noTank',
        anomalyLocation: report.anomalyLocation || '',
        customLocation: report.customLocation || '',
        customText: report.customText || '',
        limitations: report.limitations || [],
        findings: {
          vent: false,
          fill: false,
          distribution: false,
          ...report.findings
        },
        imageUrls: report.imageUrls || [], // Load image URLs from the report
        fullDocumentText: report.fullDocumentText || '',
        resources: report.resources || [], // Load resources from the report
      };

      // Clear existing localStorage before loading new data
      localStorage.removeItem('tankFormData');
      setFormData(formattedData);
      
      // Clear existing previews
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
      setImagePreviews([]);
      setImageFiles([]);
      
      // Load images from URLs
      if (formattedData.imageUrls?.length > 0) {
        console.log('Loading images from URLs:', formattedData.imageUrls);
        const newPreviews = [];
        const newFiles = [];
        
        for (const imageData of formattedData.imageUrls) {
          try {
            console.log('Fetching image:', imageData.url);
            const response = await fetch(imageData.url);
            if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
            const blob = await response.blob();
            const previewUrl = URL.createObjectURL(blob);
            newPreviews.push(previewUrl);
            
            // Create a new file from the blob
            const fileName = imageData.path.split('/').pop();
            const file = new File([blob], fileName, { type: blob.type });
            newFiles.push(file);
            
            console.log('Successfully loaded image:', fileName);
          } catch (error) {
            console.error('Error loading image:', imageData.url, error);
            toast.error(`Error loading image: ${error.message}`);
          }
        }
        
        setImagePreviews(newPreviews);
        setImageFiles(newFiles);
        console.log('Finished loading images:', newPreviews.length, 'previews created');
      }
      
      // Set editing mode if isEditing is true
      if (isEditing) {
        console.log('Setting editing mode for report:', report.id);
        setEditingReportId(report.id);
        
        // Load client and inspector data when editing
        if (report.portalClients && Array.isArray(report.portalClients)) {
          setSelectedClients(report.portalClients);
        } else {
          setSelectedClients([]);
        }
        
        // Load the selected inspectors/collaborators
        if (report.collaborators && Array.isArray(report.collaborators)) {
          setSelectedInspectors(report.collaborators);
        } else {
          setSelectedInspectors([]);
        }
        
        toast.success('Draft loaded for editing');
      } else {
        setEditingReportId(null);
        // Clear selected clients and inspectors if not editing
        setSelectedClients([]);
        setSelectedInspectors([]);
      }
      
      // Add nicer transition effect
      const formElement = document.querySelector('form');
      formElement.classList.add('bg-gradient-to-r', 'from-blue-50', 'to-cyan-50', 'transition-all', 'duration-500');
      setTimeout(() => {
        formElement.classList.remove('bg-gradient-to-r', 'from-blue-50', 'to-cyan-50');
      }, 1000);
    } catch (error) {
      console.error('Error loading report:', error);
      toast.error('Error loading report');
    } finally {
      setIsLoadingReport(false);
    }
  };

  // Fix the deleteReport function to ensure it works properly without forced refreshes
  const deleteReport = async (reportId, e) => {
    e.stopPropagation(); // Prevent triggering load when clicking delete
    
    // Check if the report has images or resources
    const reportRef = doc(db, 'inspections', reportId);
    const reportSnap = await getDoc(reportRef);
    
    if (!reportSnap.exists()) {
      console.error('Report does not exist:', reportId);
      toast.error('This report has already been deleted or does not exist.');
      return;
    }
    
    const reportData = reportSnap.data();
    
    // Check if user is a collaborator and prevent deletion (additional safeguard)
    const isCreator = reportData.createdBy === user.uid;
    const isCollaborator = reportData.collaborators?.some(
      collaborator => 
        collaborator.id === user.uid || 
        (typeof collaborator === 'string' && collaborator === user.uid)
    );
    
    if (!isCreator && isCollaborator) {
      toast.error('Collaborators cannot delete reports. Please contact the report creator.');
      return;
    }
    
    const hasImages = reportData.imageUrls?.length > 0;
    const hasResources = reportData.resources?.length > 0;
    const imageCount = hasImages ? reportData.imageUrls.length : 0;
    const resourceCount = hasResources ? reportData.resources.length : 0;
    
    // Create an enhanced multi-step confirmation message
    let deletionDetails = '';
    
    // Base confirmation message about the report itself
    const reportConfirmation = `Are you sure you want to delete the report for "${reportData.clientName || 'Unknown Client'}" at "${reportData.propertyAddress || 'Unknown Address'}"?`;
    
    // Add details about images if present
    if (hasImages) {
      deletionDetails += `\n\n• This will permanently delete ${imageCount} image${imageCount !== 1 ? 's' : ''}.`;
    }
    
    // Add details about resource files if present
    if (hasResources) {
      // Count file vs link resources for a more accurate message
      const fileResources = reportData.resources.filter(r => r.type === 'file').length;
      const linkResources = resourceCount - fileResources;
      
      if (fileResources > 0) {
        deletionDetails += `\n• This will permanently delete ${fileResources} resource file${fileResources !== 1 ? 's' : ''}.`;
      }
      
      if (linkResources > 0) {
        deletionDetails += `\n• This will remove ${linkResources} resource link${linkResources !== 1 ? 's' : ''}.`;
      }
    }
    
    // Add final warning if there are images or resources
    if (hasImages || hasResources) {
      deletionDetails += `\n\nWARNING: Deleted files CANNOT be recovered, even if you restore the report later.`;
    }
    
    // Final confirmation message
    const confirmMessage = reportConfirmation + deletionDetails;
    
    // Show confirmation dialog
    if (confirm(confirmMessage)) {
      try {
        console.log('🗑️ [Archive] Archiving report:', reportId);
        
        // Track success/failure for each step
        let resourcesDeletionSuccess = true;
        let archivingSuccess = true;
        let notificationSuccess = true;
        
        // 1. Delete all associated images from storage first
        if (hasImages) {
          console.log('📸 Found', reportData.imageUrls.length, 'images to delete');
          try {
            const deleteImagePromises = reportData.imageUrls.map(async (imageData) => {
              try {
                await deleteImageFromStorage(imageData);
                console.log('✅ [Delete] Deleted image:', imageData.path || imageData.url);
                return { success: true, path: imageData.path || imageData.url };
              } catch (error) {
                // Check if this is a 404/not-found error
                if (error.code === 'storage/object-not-found') {
                  console.log('ℹ️ [Delete] Image already deleted or does not exist:', imageData.path || imageData.url);
                  // This is not a true failure - the image doesn't exist anyway
                  return { success: true, path: imageData.path || imageData.url, status: 'already_deleted' };
                } else {
                  // Log the error but don't throw - allow the deletion to continue
                  console.error('❌ [Delete] Error deleting image:', imageData, error);
                  return { success: false, path: imageData.path || imageData.url, error: error.message };
                }
              }
            });

            // Wait for all image deletions to complete
            await Promise.allSettled(deleteImagePromises);
          } catch (imageError) {
            console.error('Error during image deletion:', imageError);
          }
        }
        
        // 2. Delete all associated resource files from storage
        if (hasResources) {
          console.log('📄 Found', resourceCount, 'resources to delete');
          try {
            const deleteResourcePromises = reportData.resources.map(async (resource) => {
              // Only delete files, not links
              if (resource.type === 'file' && resource.path) {
                try {
                  const fileRef = ref(storage, resource.path);
                  await deleteObject(fileRef);
                  console.log('✅ [Delete] Deleted resource file:', resource.path);
                  resourcesDeletionSuccess = true;
                  return { success: true, path: resource.path };
                } catch (error) {
                  // Check if this is a 404/not-found error, which is actually fine
                  // (means the file was already deleted)
                  if (error.code === 'storage/object-not-found') {
                    console.log('ℹ️ [Delete] Resource file already deleted or does not exist:', resource.path);
                    // This is not a true failure - the resource doesn't exist anyway
                    return { success: true, path: resource.path, status: 'already_deleted' };
                  } else {
                    // Log the error but don't throw - allow the deletion to continue
                    console.error('❌ [Delete] Error deleting resource file:', resource, error);
                    resourcesDeletionSuccess = false;
                    return { success: false, path: resource.path, error: error.message };
                  }
                }
              } else {
                // For non-file resources (like links), just log and continue
                console.log('ℹ️ [Delete] Skipping non-file resource:', resource.type);
                return { success: true, skipped: true, resource: resource.type };
              }
            });
            
            // Wait for all resource file deletions to complete
            await Promise.allSettled(deleteResourcePromises);
          } catch (resourceError) {
            console.error('Error during resource deletion:', resourceError);
            resourcesDeletionSuccess = false;
          }
        }

        // 3. Create the archive document (try-catch so we still delete even if this fails)
        try {
          // Prepare the archived data
          const archivedData = {
            ...reportData,
            originalId: reportId,
            deletedAt: new Date().toISOString(),
            deletedBy: 'user',
            _archived: true,
            // Remove image URLs from the archived data since the actual images are deleted
            // But keep a count of how many images were in the original report
            imageCount: reportData.imageUrls?.length || 0,
            imageUrls: [], // Clear image URLs since the actual files are deleted
            resourceCount: reportData.resources?.length || 0,
            resources: reportData.resources ? reportData.resources.map(resource => {
              // Create a clean copy without undefined values
              const cleanResource = { ...resource };
              
              // For file resources, keep metadata but remove the actual file path
              if (resource.type === 'file') {
                // Set path to empty string instead of undefined to avoid Firestore errors
                cleanResource.path = '';
              }
              
              // Filter out any fields with undefined values
              Object.keys(cleanResource).forEach(key => {
                if (cleanResource[key] === undefined) {
                  delete cleanResource[key];
                }
              });
              
              return cleanResource;
            }) : [] // Keep resource metadata but remove file paths
          };
          
          // Clean the object recursively to remove any undefined values that could cause Firestore errors
          const cleanObjectForFirestore = (obj) => {
            const cleanObj = { ...obj };
            
            Object.keys(cleanObj).forEach(key => {
              const value = cleanObj[key];
              
              // If value is undefined or null, remove it
              if (value === undefined) {
                delete cleanObj[key];
              }
              // If value is an object (but not an array or null), recursively clean it
              else if (value && typeof value === 'object' && !Array.isArray(value)) {
                cleanObj[key] = cleanObjectForFirestore(value);
              }
              // If value is an array, clean each element if it's an object
              else if (Array.isArray(value)) {
                cleanObj[key] = value.map(item => 
                  item && typeof item === 'object' ? cleanObjectForFirestore(item) : item
                );
              }
            });
            
            return cleanObj;
          };
          
          // Clean the archived data before adding to Firestore
          const cleanArchivedData = cleanObjectForFirestore(archivedData);
          
          // Add to deletedReports collection
          const deletedReportsRef = collection(db, 'deletedReports');
          const archiveDoc = await addDoc(deletedReportsRef, cleanArchivedData);
          
          console.log('✅ [Archive] Report archived successfully with ID:', archiveDoc.id);
        } catch (archiveError) {
          console.error('❌ [Archive] Error archiving report:', archiveError);
          archivingSuccess = false;
          // Continue with deletion even if archiving fails
        }
        
        // 4. Notify portal users about the report being removed
        try {
          const result = await notifyReportRemoved(reportId, db, portalDb);
          if (result.success) {
            console.log('✅ [Portal] Portal users notified:', result.message);
          } else {
            console.error('❌ [Portal] Error notifying portal users:', result.error);
            notificationSuccess = false;
          }
        } catch (notifyError) {
          console.error('❌ [Portal] Error notifying portal users:', notifyError);
          notificationSuccess = false;
          // Continue with deletion even if notification fails
        }
        
        // 5. Delete the original document - do this regardless of previous steps
        try {
          await deleteDoc(reportRef);
          console.log('✅ [Delete] Original report deleted successfully');
          
          // Clear local state if this was the report being edited
          if (editingReportId === reportId) {
            setEditingReportId(null);
            setFormData({
              clientName: '',
              dateOfInspection: new Date().toISOString().split('T')[0],
              propertyAddress: '',
              inspectionType: 'standard',
              gprScope: 'full',
              gprTargetedLocation: '',
              gprTargetedReason: '',
              gprTargetedReasonOther: '',
              scenario: 'noTank',
              anomalyLocation: '',
              customLocation: '',
              customText: '',
              limitations: [],
              imageUrls: [],
              resources: []
            });
            
            // Clear local image previews and files if this was the edited report
            imagePreviews.forEach(url => URL.revokeObjectURL(url));
            setImageFiles([]);
            setImagePreviews([]);
            localStorage.removeItem('tankFormData');
          }
          
          // Show a success message with appropriate details
          let successMessage = 'Report deleted successfully.';
          if (!archivingSuccess) {
            successMessage += ' (Note: Error during archiving)';
          }
          if (!resourcesDeletionSuccess) {
            successMessage += ' (Note: Some resources may not have been fully deleted)';
          }
          
          toast.success(successMessage);
          
          // Refresh the reports list to ensure we have the latest data
          // This is especially important in app environments where the page doesn't refresh
          await loadInitialData();
          
        } catch (deleteError) {
          console.error('❌ [Delete] Error deleting original report:', deleteError);
          toast.error('Error deleting report: ' + deleteError.message);
        }
        
      } catch (error) {
        console.error('Error in delete process:', error);
        toast.error('Error deleting report: ' + error.message);
      }
    }
  };

  const deleteDraft = async (draftId, e) => {
    e.stopPropagation();
    if (confirm('Are you sure you want to delete this draft? This will also delete all associated images.')) {
      try {
        // Get the draft data before deleting
        const draftRef = doc(db, 'drafts', draftId);
        const draftSnap = await getDoc(draftRef);
        
        if (draftSnap.exists()) {
          const draftData = draftSnap.data();
          console.log('🗑️ [Delete] Deleting draft:', draftId);
          
          // Delete all associated images from storage first
          if (draftData.imageUrls?.length > 0) {
            console.log('📸 Found', draftData.imageUrls.length, 'images to delete');
            const deletePromises = draftData.imageUrls.map(async (imageData) => {
              try {
                await deleteImageFromStorage(imageData);
                console.log('✅ [Delete] Deleted image:', imageData.path || imageData.url);
              } catch (error) {
                console.error('❌ [Delete] Error deleting image:', imageData, error);
              }
            });

            // Wait for all image deletions to complete
            await Promise.allSettled(deletePromises);
          }

          // Delete the draft document
          await deleteDoc(draftRef);
          setDrafts(prev => prev.filter(d => d.id !== draftId));
          toast.success('Draft and associated images deleted');
        }
      } catch (error) {
        console.error('❌ [Delete] Error deleting draft:', error);
        toast.error('Error deleting draft');
      }
    }
  };

  const renameDraft = async (draftId, newName) => {
    try {
      await updateDoc(doc(db, 'drafts', draftId), {
        clientName: newName
      });
      setDrafts(prev => prev.map(d => 
        d.id === draftId ? { ...d, clientName: newName } : d
      ));
      setEditingDraftId(null);
    } catch (error) {
      console.error('Error renaming draft:', error);
      toast.error('Error renaming draft');
    }
  };

  // Update the PreviewModal component
  const clearForm = async ({ 
    shouldConfirm = true, 
    deleteImages = true,
    clearEditMode = true 
  } = {}) => {
    try {
      // If confirmation is required and user declines, return early
      if (shouldConfirm && !confirm('Are you sure you want to clear the form?')) {
        return;
      }

      // If we have images and deleteImages is true
      if (deleteImages && formData.imageUrls?.length) {
        for (const image of formData.imageUrls) {
          const isUsedInSavedContent = drafts.some(draft => 
            draft.imageUrls?.some(draftImg => 
              draftImg.url === image.url || draftImg.path === image.path
            )
          ) || recentReports.some(report => 
            report.imageUrls?.some(reportImg => 
              reportImg.url === image.url || reportImg.path === image.path
            )
          );

          if (!isUsedInSavedContent && !editingReportId) {
            try {
              await deleteImageFromStorage(image);
            } catch (error) {
              console.error('Failed to delete image:', image, error);
              // Don't throw, just log the error and continue
            }
          }
        }
        // Log to console instead of showing toast
        console.log(`Cleared ${formData.imageUrls.length} images from form`);
      }

      // Clear localStorage
      localStorage.removeItem('tankFormData');

      // Reset form state
      setFormData({
        clientName: '',
        dateOfInspection: new Date().toISOString().split('T')[0],
        propertyAddress: '',
        inspectionType: 'standard',
        gprScope: 'full',
        gprTargetedLocation: '',
        gprTargetedReason: '',
        gprTargetedReasonOther: '',
        scenario: 'noTank',
        anomalyLocation: '',
        customLocation: '',
        customText: '',
        limitations: [],
        findings: {
          vent: false,
          fill: false,
          distribution: false
        },
        fullDocumentText: '',
        imageUrls: [],
        resources: [], // Reset resources
      });

      // Clear image previews
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
      setImageFiles([]);
      setImagePreviews([]);
      setImageUploadProgress({});
      
      // Clear selected clients and inspectors
      setSelectedClients([]);
      setSelectedInspectors([]);

      // Clear edit mode if requested
      if (clearEditMode) {
        setEditingReportId(null);
        setExistingReport(null); // Also clear existingReport data
      }

      // Only show one toast notification with the most important information
      toast.success('Form ready for new report');
    } catch (error) {
      console.error('Error clearing form:', error);
      toast.error('Error clearing form');
    }
  };

  // Replace the existing clear functions with calls to the new one
  const quickClear = () => {
    const hasContent = formData.clientName || formData.propertyAddress || formData.customText;
    clearForm({ 
      shouldConfirm: hasContent, 
      deleteImages: true,
      clearEditMode: true 
    });
  };

  const handleNewClick = () => {
    clearForm({ 
      shouldConfirm: true, 
      deleteImages: true,
      clearEditMode: true 
    });
  };

  const cancelEdit = () => {
    if (editingReportId) {
      // Get the original report we were editing
      const originalReport = recentReports.find(r => r.id === editingReportId);
      
      if (originalReport && formData.imageUrls?.length) {
        // Find images that were added during editing (not in the original report)
        const newlyAddedImages = formData.imageUrls.filter(currentImage => {
          // Check if this image was not in the original report
          return !originalReport.imageUrls?.some(origImage => 
            origImage.path === currentImage.path
          );
        });
        
        // Delete newly added images from storage
        if (newlyAddedImages.length) {
          Promise.all(newlyAddedImages.map(image => 
            deleteImageFromStorage(image).catch(err => 
              console.error('Error deleting image during cancel:', err)
            )
          ));
          console.log(`Cleaned up ${newlyAddedImages.length} images added during editing`);
        }
      }
      
      // Clear existing report reference
      setExistingReport(null);
    }

    // Then proceed with normal form clearing
    clearForm({ 
      shouldConfirm: true, 
      deleteImages: false, // Keep this false to avoid deleting original images
      clearEditMode: true 
    });
  };

  // Update the loadMoreReports function
  const loadMoreReports = async () => {
    if (!hasMore || !recentReports.length) return;

    try {
      const lastReport = recentReports[recentReports.length - 1];
      const lastSnapshot = await getDoc(doc(db, 'inspections', lastReport.id));
      
      const reportsRef = collection(db, 'inspections');
      
      // Query for owned reports
      const ownedReportsQuery = query(
        reportsRef,
        where('createdBy', '==', user?.uid),
        orderBy('dateOfInspection', 'desc'),
        startAfter(lastSnapshot),
        limit(REPORTS_PER_PAGE)
      );

      const ownedSnapshot = await getDocs(ownedReportsQuery);
      const newOwnedReports = ownedSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Create a map to ensure we don't have duplicates
      const newReportsMap = new Map();
      
      // Add owned reports to the map
      newOwnedReports.forEach(report => {
        newReportsMap.set(report.id, report);
      });
      
      // Get all reports after the last loaded report to find collaborations
      const allReportsQuery = query(
        reportsRef,
        orderBy('dateOfInspection', 'desc'),
        startAfter(lastSnapshot),
        limit(100) // Set a reasonable limit to avoid loading too much data
      );
      
      const allSnapshot = await getDocs(allReportsQuery);
      
      // Filter for reports where user is a collaborator
      let newCollaboratorReports = [];
      
      allSnapshot.docs.forEach(doc => {
        const data = doc.data();
        
        // Skip if it's created by the user (already included in owned reports)
        if (data.createdBy === user?.uid) return;
        
        // Check if user is a collaborator using the helper function
        const isCollaborator = checkIfUserIsCollaborator(data.collaborators, user?.uid);
        
        if (isCollaborator) {
          newCollaboratorReports.push({
            id: doc.id,
            ...data,
            isCollaborator: true
          });
        }
      });
      
      // Add collaborator reports to the map
      newCollaboratorReports.forEach(report => {
        newReportsMap.set(report.id, report);
      });
      
      // Convert to array and sort by date of inspection
      const newReports = Array.from(newReportsMap.values()).sort((a, b) => {
        const dateA = a.dateOfInspection ? new Date(a.dateOfInspection) : new Date(0);
        const dateB = b.dateOfInspection ? new Date(b.dateOfInspection) : new Date(0);
        return dateB - dateA;
      });
      
      setRecentReports(prev => [...prev, ...newReports]);
      
      // Determine if there are more reports to load
      setHasMore(newReports.length > 0);
    } catch (error) {
      console.error('Error loading more reports:', error);
      toast.error('Failed to load more reports');
    }
  };

  // Add cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (autoClearTimeoutRef.current) {
        clearTimeout(autoClearTimeoutRef.current);
      }
    };
  }, []);

  const handleEditImage = (index) => {
    setEditingImage(imagePreviews[index]);
    setEditingImageIndex(index);
  };

  const handleSaveEdit = async (editedImageBlob) => {
    try {
      const toastId = toast.loading('Saving edited image...');
      
      // First, delete the old image from Firebase Storage
      const oldImageData = formData.imageUrls[editingImageIndex];
      if (oldImageData) {
        await deleteImageFromStorage(oldImageData);
      }

      // Create a new file from the edited blob
      const newFile = new File([editedImageBlob], `edited-image-${Date.now()}.jpg`, {
        type: 'image/jpeg'
      });

      // Upload the new image to Firebase Storage
      const downloadURL = await uploadImageToStorage(newFile);
      const newImageData = { 
        url: downloadURL, 
        path: `images/${Date.now()}-edited-image.jpg` 
      };

      // Update formData with the new image URL
      setFormData(prev => {
        const newImageUrls = [...prev.imageUrls];
        newImageUrls[editingImageIndex] = newImageData;
        return {
          ...prev,
          imageUrls: newImageUrls
        };
      });

      // Update the preview
      const newPreviewUrl = URL.createObjectURL(editedImageBlob);
      setImagePreviews(prev => {
        const newPreviews = [...prev];
        // Revoke old preview URL
        URL.revokeObjectURL(prev[editingImageIndex]);
        newPreviews[editingImageIndex] = newPreviewUrl;
        return newPreviews;
      });
      
      // Update the files array
      setImageFiles(prev => {
        const newFiles = [...prev];
        newFiles[editingImageIndex] = newFile;
        return newFiles;
      });
      
      setEditingImage(null);
      setEditingImageIndex(null);
      
      toast.success('Image saved successfully!', { id: toastId });
    } catch (error) {
      console.error('Error saving edited image:', error);
      toast.error('Failed to save edited image. Please try again.');
    }
  };

  // Update the README content fetching
  useEffect(() => {
    const fetchReadme = async () => {
      try {
        // Use APP_VERSION and current timestamp for stronger cache busting
        const cacheBuster = `${APP_VERSION}-${Date.now()}`;
        const response = await fetch(`/README.md?v=${cacheBuster}`);
        if (!response.ok) throw new Error('Failed to fetch README');
        const text = await response.text();
        setChangelogContent(text);
      } catch (error) {
        console.error('Error loading README:', error);
        setChangelogContent('Error loading changelog');
      }
    };
    
    if (showChangelog) {
      fetchReadme();
    }
  }, [showChangelog]);

  // Add handleShare function
  const handleShare = (report) => {
    if (report.id) {
      navigate(`/share-report/${report.id}`);
    } else {
      navigate('/share-report', { state: { reportData: report } });
    }
  };

  // Add resetViewCount function
  const resetViewCount = async (reportId) => {
    if (!user) return;
    try {
      const reportRef = doc(db, 'inspections', reportId);
      const now = new Date().toISOString();
      
      // Update Firestore to clear both view and download counts
      await updateDoc(reportRef, {
        viewCount: 0,
        viewLog: [],
        downloadCount: 0,
        downloadLog: [],
        updatedAt: now
      });
      
      toast.success('Counters reset successfully');
      setViewModalReport(null);
    } catch (error) {
      console.error('Error resetting counters:', error);
      toast.error('Failed to reset counters');
    }
  };

  // Add togglePaidStatus function
  const togglePaidStatus = async (reportId) => {
    if (!user) return;
    try {
      const reportRef = doc(db, 'inspections', reportId);
      
      // Get current report data
      const reportDoc = await getDoc(reportRef);
      if (!reportDoc.exists()) {
        throw new Error("Report not found");
      }
      
      const currentData = reportDoc.data();
      const newPaidStatus = !currentData.isPaid;
      
      // Update local state immediately for a responsive UI
      setRecentReports(prevReports => 
        prevReports.map(report => 
          report.id === reportId 
            ? {...report, isPaid: newPaidStatus} 
            : report
        )
      );
      
      // Force a component update for immediate visual feedback
      setForceUpdate(prev => !prev);
      
      // Update Firestore with new paid status
      await updateDoc(reportRef, {
        isPaid: newPaidStatus,
        updatedAt: new Date().toISOString()
      });
      
      toast.success(newPaidStatus ? 'Report marked as paid' : 'Report marked as unpaid');
    } catch (error) {
      console.error('Error updating payment status:', error);
      toast.error('Failed to update payment status');
      
      // Revert local state on error
      setRecentReports(prevReports => 
        prevReports.map(report => 
          report.id === reportId 
            ? {...report, isPaid: !report.isPaid} 
            : report
        )
      );
    }
  };

  useEffect(() => {
    let unsubscribes = [];
    
    // Set up listeners for view counts
    const setupViewCountListeners = () => {
      // Clear any existing listeners
      unsubscribes.forEach(unsub => unsub());
      unsubscribes = [];
      
      // Set up a listener for each report
      recentReports.forEach(report => {
        const unsubscribe = onSnapshot(
          doc(db, 'inspections', report.id),
          (doc) => {
            if (doc.exists()) {
              setViewCounts(prev => ({
                ...prev,
                [doc.id]: doc.data().viewCount || 0
              }));
            }
          },
          (error) => {
            console.error('Error in view count listener:', error);
          }
        );
        unsubscribes.push(unsubscribe);
      });
    };

    if (recentReports.length > 0) {
      setupViewCountListeners();
    }

    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [recentReports]);

  const handleClientSelect = (clients) => {
    setSelectedClients(clients);
  };

  // Function to handle opening the client sharing modal
  const handleOpenClientModal = (report) => {
    setShareClientModalReport(report);
    
    // Load the currently selected clients for this report
    if (report.portalClients && Array.isArray(report.portalClients)) {
      setSelectedClients(report.portalClients);
    } else {
      setSelectedClients([]);
    }
    
    // Load collaborators for this report
    if (report.collaborators && Array.isArray(report.collaborators)) {
      setSelectedInspectors(report.collaborators);
    } else {
      setSelectedInspectors([]);
    }
    
    // Reset the active tab to clients
    setSearchActiveTab('clients');
  };

  // Function to handle sharing with selected clients
  const handleUpdateClients = async () => {
    if (!shareClientModalReport) return;
    
    setIsUpdatingClients(true);
    const toastId = toast.loading(searchActiveTab === 'clients' ? 'Updating client access...' : 'Updating collaborators...');
    
    try {
      const reportId = shareClientModalReport.id;
      const reportRef = doc(db, 'inspections', reportId);
      
      // First, get the full report data to make sure we have everything needed
      const reportSnap = await getDoc(reportRef);
      if (!reportSnap.exists()) {
        throw new Error(`Report with ID ${reportId} not found`);
      }
      
      const reportData = reportSnap.data();
      
      if (searchActiveTab === 'clients') {
        // DEBUGGING - Log what we're trying to update
        console.log('🔄 Updating client access for report:', reportId);
        console.log('👥 Selected clients:', selectedClients);
        console.log('📊 Current report data:', reportData);
        
        // Update the report with the new client list
        await updateDoc(reportRef, {
          portalClients: selectedClients.map(client => ({
            id: client.id,
            firstName: client.firstName,
            lastName: client.lastName,
            email: client.email
          })),
          updatedAt: new Date().toISOString()
        });
        
        console.log('✅ Updated report document with new client list');
        
        // Get the portal database reference - IMPORTANT: this is a different database
        const { portalDb } = await import('../utils/portalFirebase');
        
        // Process client changes in the portal database
        try {
          // Get previous clients to determine changes
          const previousClients = reportData.portalClients || [];
          
          // Clients to add (new clients not in previous list)
          const clientsToAdd = selectedClients.filter(
            newClient => !previousClients.some(oldClient => oldClient.id === newClient.id)
          );
          
          // Clients to remove (clients in previous list but not in new list)
          const clientsToRemove = previousClients.filter(
            oldClient => !selectedClients.some(newClient => newClient.id === oldClient.id)
          );
          
          console.log('➕ Clients to add:', clientsToAdd);
          console.log('➖ Clients to remove:', clientsToRemove);
          
          // Create a portal inspection document if it doesn't exist
          const portalReportRef = doc(portalDb, 'inspections', reportId);
          const portalReportSnap = await getDoc(portalReportRef);
          
          if (!portalReportSnap.exists()) {
            // Create the inspection document in the portal database
            console.log('🆕 Creating inspection in portal database');
            
            // Only include necessary fields for the portal
            const portalReportData = {
              id: reportId,
              clientName: reportData.clientName || '',
              propertyAddress: reportData.propertyAddress || '',
              dateOfInspection: reportData.dateOfInspection || new Date().toISOString(),
              scenario: reportData.scenario || 'noTank',
              imageUrls: reportData.imageUrls || [],
              createdAt: reportData.createdAt || new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              inspectorName: reportData.inspectorName || 'Oil Tank Sweep',
              isPaid: reportData.isPaid ?? true,
              fullDocumentText: reportData.fullDocumentText || '',
              pdfUrl: reportData.pdfUrl || '',
              limitations: reportData.limitations || []
            };
            
            await setDoc(portalReportRef, portalReportData);
            console.log('✅ Created portal inspection document');
          } else {
            // Update the existing portal inspection document with latest data
            console.log('🔄 Updating existing portal inspection document');
            
            await updateDoc(portalReportRef, {
              clientName: reportData.clientName || '',
              propertyAddress: reportData.propertyAddress || '',
              dateOfInspection: reportData.dateOfInspection || new Date().toISOString(),
              scenario: reportData.scenario || 'noTank',
              imageUrls: reportData.imageUrls || [],
              updatedAt: new Date().toISOString(),
              inspectorName: reportData.inspectorName || 'Oil Tank Sweep',
              isPaid: reportData.isPaid ?? true,
              fullDocumentText: reportData.fullDocumentText || '',
              pdfUrl: reportData.pdfUrl || '',
              limitations: reportData.limitations || []
            });
            
            console.log('✅ Updated portal inspection document');
          }
          
          // Process client changes
          const updatePromises = [];
          
          // Add report to new clients
          for (const client of clientsToAdd) {
            const userRef = doc(portalDb, 'users', client.id);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              console.log(`✅ Adding report ${reportId} to user ${client.id}`);
              
              // Check if the user already has the report
              const userData = userSnap.data();
              const savedReports = userData.savedReports || [];
              
              if (!savedReports.includes(reportId)) {
                // Add the report to the user's savedReports array
                updatePromises.push(
                  updateDoc(userRef, {
                    savedReports: arrayUnion(reportId)
                  })
                );
                
                // Also send notification to the client
                updatePromises.push(
                  notifyReportShared(reportId, client.email, db, portalDb)
                    .then(result => {
                      if (result.success) {
                        console.log(`Notification sent to client: ${client.email}`);
                      } else {
                        console.error(`Failed to notify client: ${client.email}`, result.error);
                      }
                    })
                );
              } else {
                console.log(`ℹ️ User ${client.id} already has report ${reportId}`);
              }
            } else {
              console.warn(`⚠️ User ${client.id} not found in portal database`);
            }
          }
          
          // Remove report from clients who were removed
          for (const client of clientsToRemove) {
            const userRef = doc(portalDb, 'users', client.id);
            const userSnap = await getDoc(userRef);
            
            if (userSnap.exists()) {
              console.log(`🗑️ Removing report ${reportId} from user ${client.id}`);
              
              // Check if the user has the report
              const userData = userSnap.data();
              const savedReports = userData.savedReports || [];
              
              if (savedReports.includes(reportId)) {
                // Remove the report from the user's savedReports array
                updatePromises.push(
                  updateDoc(userRef, {
                    savedReports: arrayRemove(reportId)
                  })
                );
              } else {
                console.log(`ℹ️ User ${client.id} doesn't have report ${reportId}`);
              }
            } else {
              console.warn(`⚠️ User ${client.id} not found in portal database when removing access`);
            }
          }
          
          // Wait for all promises to resolve
          await Promise.all(updatePromises);
          console.log('✅ Successfully completed all client access updates');
          
          // Update the report in the local state
          setRecentReports(prev => 
            prev.map(r => 
              r.id === reportId 
                ? { ...r, portalClients: selectedClients }
                : r
            )
          );
          
          toast.success('Client access updated successfully!', { id: toastId });
        } catch (portalError) {
          console.error('❌ Error updating portal database:', portalError);
          toast.error('Error syncing with client portal', { id: toastId });
        }
      } else if (searchActiveTab === 'inspectors') {
        // Updating collaborators
        console.log('🔄 Updating collaborators for report:', reportId);
        console.log('👥 Selected inspectors:', selectedInspectors);
        
        // Make sure collaborators have a consistent format
        const formattedCollaborators = selectedInspectors.map(inspector => {
          // If inspector is just a string, convert to object format
          if (typeof inspector === 'string') {
            return { id: inspector };
          }
          
          // Ensure the inspector object has all required fields
          return {
            id: inspector.id,
            displayName: inspector.displayName || 
                         `${inspector.firstName || ''} ${inspector.lastName || ''}`.trim() || 
                         inspector.email || 
                         'Collaborator',
            email: inspector.email || ''
          };
        });
        
        // Update the report with the new collaborators list
        await updateDoc(reportRef, {
          collaborators: formattedCollaborators,
          collaboratorIds: formattedCollaborators.map(c => c.id).filter(Boolean),
          updatedAt: new Date().toISOString()
        });
        
        console.log('✅ Updated report document with new collaborators list:', formattedCollaborators);
        
        // Update the report in the local state
        setRecentReports(prev => 
          prev.map(r => 
            r.id === reportId 
              ? { ...r, collaborators: formattedCollaborators }
              : r
          )
        );
        
        toast.success('Collaborators updated successfully!', { id: toastId });
      }
      
      // Close the modal after successful update
      setShareClientModalReport(null);
      // Clear the selected clients and inspectors states after updating
      setSelectedClients([]);
      setSelectedInspectors([]);
    } catch (error) {
      console.error('❌ Failed to update report:', error);
      toast.error('Failed to update access', { id: toastId });
    } finally {
      setIsUpdatingClients(false);
    }
  };

  // Add function to open report modal
  const openReportDetails = (reportId, e) => {
    // Prevent triggering other click handlers (like edit, delete, etc.)
    e.stopPropagation();
    setSelectedReportId(reportId);
    setShowReportModal(true);
    
    // Reset unread message count for this report in the UI
    if (unreadMessagesMap[reportId] && unreadMessagesMap[reportId] > 0) {
      setUnreadMessagesMap(prev => ({
        ...prev,
        [reportId]: 0 // Reset the count for this report
      }));
    }
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setSelectedReportId(null);
  };

  // Add handler for selecting/deselecting inspectors
  const handleInspectorSelect = (inspector, isRemove = false) => {
    if (isRemove) {
      setSelectedInspectors(prev => prev.filter(i => i.id !== inspector.id));
    } else {
      // Add inspector only if not already in the list
      if (!selectedInspectors.some(i => i.id === inspector.id)) {
        setSelectedInspectors(prev => [...prev, inspector]);
      }
    }
  };

  // Add this function to prepare collaborators data for saving
  const prepareCollaboratorsData = () => {
    return selectedInspectors.map(inspector => {
      // If inspector is just a string, convert to object format
      if (typeof inspector === 'string') {
        return { id: inspector };
      }
      
      // Ensure the inspector object has all required fields
      return {
        id: inspector.id,
        displayName: inspector.displayName || 
                     `${inspector.firstName || ''} ${inspector.lastName || ''}`.trim() || 
                     inspector.email || 
                     'Collaborator',
        email: inspector.email || ''
      };
    });
  };

  // Find the current editReport function and update it to load collaborators and handle permissions
  const editReport = async (reportId) => {
    try {
      setIsLoading(true);
      setEditingReportId(reportId);
      
      const reportRef = doc(db, 'inspections', reportId);
      const reportSnap = await getDoc(reportRef);
      
      if (reportSnap.exists()) {
        const data = reportSnap.data();
        
        // Check if the current user is allowed to edit this report
        // They can edit if they are the creator or a collaborator
        const isCreator = data.createdBy === user.uid;
        const isCollaborator = checkIfUserIsCollaborator(data.collaborators, user.uid);
        
        if (!isCreator && !isCollaborator) {
          toast.error('You do not have permission to edit this report');
          setIsLoading(false);
          return;
        }
        
        // Store the existing report for future reference
        setExistingReport(data);
        
        // Load the form data from the report
        const loadedFormData = {
          clientName: data.clientName || '',
          propertyAddress: data.propertyAddress || '',
          dateOfInspection: data.dateOfInspection || new Date().toISOString().split('T')[0],
          inspectionType: data.inspectionType || 'standard',
          gprScope: data.gprScope || 'full',
          gprTargetedLocation: data.gprTargetedLocation || '',
          gprTargetedReason: data.gprTargetedReason || '',
          gprTargetedReasonOther: data.gprTargetedReasonOther || '',
          scenario: data.scenario || 'noTank',
          anomalyLocation: data.anomalyLocation || '',
          customLocation: data.customLocation || '',
          customText: data.customText || '',
          limitations: data.limitations || [],
          findings: data.findings || {
            vent: false,
            fill: false,
            distribution: false,
          },
          fullDocumentText: data.fullDocumentText || '',
          imageUrls: data.imageUrls || [],
          resources: data.resources || [],
        };
        
        setFormData(loadedFormData);
        
        // Load the selected clients
        if (data.portalClients && Array.isArray(data.portalClients)) {
          setSelectedClients(data.portalClients);
        } else {
          setSelectedClients([]);
        }
        
        // Load the selected inspectors/collaborators
        if (data.collaborators && Array.isArray(data.collaborators)) {
          setSelectedInspectors(data.collaborators);
        } else {
          setSelectedInspectors([]);
        }
        
        // Scroll to top
        window.scrollTo(0, 0);
        
        toast.success('Report loaded successfully!');
      } else {
        toast.error('Report not found');
      }
    } catch (error) {
      console.error('Error editing report:', error);
      toast.error('Error loading report for editing');
    } finally {
      setIsLoading(false);
    }
  };

  // Add a function to copy report link to clipboard
  const copyReportLink = async (report) => {
    if (!report.id) return;
    
    try {
      const link = `${window.location.origin}/share-report/${report.id}`;
      await navigator.clipboard.writeText(link);
      toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Error copying link:', error);
      toast.error('Error copying link');
    }
  };

  // Add a function to copy portal registration link to clipboard
  const copyPortalLink = async (report) => {
    if (!report.id) return;
    
    try {
      const link = `https://portal.oiltank.app/register?reportId=${report.id}`;
      await navigator.clipboard.writeText(link);
      toast.success('Portal registration link copied to clipboard!');
    } catch (error) {
      console.error('Error copying portal link:', error);
      toast.error('Error copying portal link');
    }
  };

  // Add a function to fetch unread messages for reports
  useEffect(() => {
    if (!user || !recentReports || recentReports.length === 0) return;
    
    // Get the first 10 report IDs (Firebase has a limit for 'in' queries)
    const reportIds = recentReports.slice(0, 10).map(report => report.id);
    
    // Create a query for messages where the recipient is not the sender and read is false
    const messagesRef = collection(portalDb, 'reportMessages');
    
    const q = query(
      messagesRef,
      where('reportId', 'in', reportIds)
    );
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const newUnreadMessagesMap = {};
      
      querySnapshot.forEach((doc) => {
        const message = doc.data();
        // Only count messages not from the current user and that are unread
        if (message.senderId !== user.uid && !message.read) {
          if (!newUnreadMessagesMap[message.reportId]) {
            newUnreadMessagesMap[message.reportId] = 1;
          } else {
            newUnreadMessagesMap[message.reportId]++;
          }
        }
      });
      
      setUnreadMessagesMap(newUnreadMessagesMap);
    });
    
    return () => unsubscribe();
  }, [user, recentReports]);

  // For larger report lists, add listeners in batches for the remaining reports
  useEffect(() => {
    if (!user || !recentReports || recentReports.length <= 10) return;
    
    const batchSize = 10;
    const unsubscribers = [];
    
    // Process reports in batches of 10 (Firebase limit for 'in' query)
    for (let i = 10; i < recentReports.length; i += batchSize) {
      const batch = recentReports.slice(i, i + batchSize).map(report => report.id);
      
      const messagesRef = collection(portalDb, 'reportMessages');
      const q = query(messagesRef, where('reportId', 'in', batch));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        setUnreadMessagesMap(prev => {
          const updated = { ...prev };
          
          querySnapshot.forEach((doc) => {
            const message = doc.data();
            if (message.senderId !== user.uid && !message.read) {
              if (!updated[message.reportId]) {
                updated[message.reportId] = 1;
              } else {
                updated[message.reportId]++;
              }
            }
          });
          
          return updated;
        });
      });
      
      unsubscribers.push(unsubscribe);
    }
    
    return () => {
      unsubscribers.forEach(unsubscribe => unsubscribe());
    };
  }, [user, recentReports]);

  // Parse URL parameters
  useEffect(() => {
    if (user && !loading) {
      // Check if we have client and reportId in URL parameters
      const searchParams = new URLSearchParams(location.search);
      const reportId = searchParams.get('reportId');
      
      if (reportId) {
        // First check if the report exists before opening the modal
        const checkReportExists = async () => {
          try {
            const reportRef = doc(db, 'inspections', reportId);
            const reportDoc = await getDoc(reportRef);
            
            if (reportDoc.exists()) {
              // If report exists, open the modal
              setSelectedReportId(reportId);
              setShowReportModal(true);
            } else {
              // If report doesn't exist, show error
              toast.error('Report not found');
            }
            
            // Clear the URL parameters by replacing the current URL without the query parameters
            const url = new URL(window.location.href);
            url.search = '';
            window.history.replaceState({}, '', url);
          } catch (error) {
            console.error('Error checking report:', error);
            toast.error('Error loading report');
            
            // Still clear URL parameters in case of error
            const url = new URL(window.location.href);
            url.search = '';
            window.history.replaceState({}, '', url);
          }
        };
        
        checkReportExists();
      }
    }
  }, [user, loading, location.search, navigate]);

  return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 pt-12"> {/* Reduced pt-16 to pt-12 for the shorter header */}
        <Toaster position="top-right" />
        <div className="container max-w-4xl mx-auto px-4">
          {lastAutoSave && (
            <div className="text-xs text-slate-500 text-right mb-2">
              Last auto-saved: {lastAutoSave.toLocaleTimeString()}
            </div>
          )}
          <div className="max-w-4xl mx-auto">
            <Card className="shadow-xl border-0 bg-white backdrop-blur-sm">
              {/* Header */}
              <CardHeader className="text-center pb-8 border-b border-slate-100">
                <div className="flex justify-between items-center mb-8">
                  <div className="flex items-center gap-2">
                    <button 
                      onClick={() => setShowChangelog(true)}
                      className="px-3 py-1.5 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg flex items-center gap-1.5 group relative"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      v{APP_VERSION}
                      <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-3 py-1.5 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-[9999]">
                        Click to view documentation
                      </div>
                    </button>
                    <button 
                      onClick={handleNewClick}
                      className="hidden md:flex px-3 py-1.5 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg items-center gap-1.5 group relative"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      New
                      <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-3 py-1.5 rounded text-xs opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-[9999]">
                        Start a new report
                      </div>
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        setIsLoading(true);
                        await loadInitialData();
                      }}
                      className="hidden md:flex px-4 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg items-center gap-1.5 transition-colors"
                    >
                      <RotateCw className="w-4 h-4" />
                      Refresh
                    </button>
                    <Link 
                      to="/admin/notes"
                      className="px-4 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                    >
                      Manage Notes
                    </Link>
                  </div>
                </div>
                <div 
                  className="group mx-auto w-20 h-20 bg-gradient-to-tr from-blue-500 to-cyan-400 rounded-full flex items-center justify-center mb-6 shadow-lg cursor-pointer hover:from-blue-600 hover:to-cyan-500 transition-all relative"
                  onClick={quickClear}
                >
                  <img 
                    src="/oil.png" 
                    alt="OilTank.App Logo" 
                    className="w-12 h-12 object-contain brightness-0 invert" 
                  />
                  <div className="absolute -top-10 left-1/2 -translate-x-1/2 bg-slate-800 text-white px-3 py-1.5 rounded-lg text-sm opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
                    Click to quickly clear form
                  </div>
                </div>
                <div className="flex flex-col items-center">
                  <CardTitle className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-1 font-['Inter'] tracking-tight">
                    OilTank.App
                  </CardTitle>
                  <p className="text-lg text-gray-500 font-['Inter'] font-light tracking-wide">
                    Report Generator
                  </p>
                </div>
              </CardHeader>

              <CardContent className="p-8">
                <form onSubmit={(e) => { e.preventDefault(); generateReport(); }} className="space-y-8 relative">
                  {editingReportId && (
                    <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
                      <div className="flex justify-between items-center">
                        <p className="text-blue-700">Editing existing report</p>
                        <div className="flex gap-4">
                          <button
                            type="button"
                            onClick={cancelEdit}
                            className="px-4 py-1 text-blue-500 hover:text-blue-700 border border-blue-500 rounded"
                          >
                            Cancel Edit
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                  {isLoadingReport && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex items-center justify-center z-50">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    </div>
                  )}
                  {/* Client Info Section */}
                  <div className="grid md:grid-cols-2 gap-6 mt-6">
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-slate-700">
                        Client Name*
                      </label>
                      <input
                        type="text"
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white/50"
                        placeholder="Enter client's full name"
                        value={formData.clientName}
                        onChange={(e) => setFormData({ ...formData, clientName: e.target.value })}
                        required
                      />
                    </div>

                    {/* Date of Inspection Section with Dolphin */}
                    <div className="space-y-3">
                      <label className="block text-sm font-medium text-slate-700">
                        Date of Inspection*
                      </label>
                      <div className="flex items-center gap-2">
                        <div className="relative w-full">
                          <DatePicker
                            selected={parseDate(formData.dateOfInspection)}
                            onChange={(date) => setFormData({ 
                              ...formData, 
                              dateOfInspection: date.toISOString().split('T')[0] 
                            })}
                            className="w-full px-4 py-3 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white/50"
                            dateFormat="MM/dd/yyyy"
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Address Section */}
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-slate-700">
                      Subject Property Address*
                    </label>
                    <div className="relative">
                      <input
                        ref={addressInputRef}
                        type="text"
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white/50"
                        placeholder="Enter full property address"
                        value={formData.propertyAddress}
                        onChange={(e) => setFormData({ ...formData, propertyAddress: e.target.value })}
                        required
                      />
                      <MapPin className="absolute right-3 top-2.5 h-5 w-5 text-slate-400" />
                    </div>
                  </div>

                  {/* Inspection Type (Top Level) */}
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-slate-700">
                      Inspection Type
                    </label>
                    <div className="grid gap-4">
                      {[
                        { value: 'standard', label: 'Oil Tank Sweep (Metal Detection)' },
                        { value: 'gpr', label: 'Oil Tank Sweep + GPR Imaging' },
                      ].map(opt => (
                        <label
                          key={opt.value}
                          className={`flex items-center p-4 rounded-lg cursor-pointer transition-all border-2
                            ${formData.inspectionType === opt.value ? 'bg-cyan-50 border-cyan-500' : 'bg-white/50 border-slate-200 hover:border-cyan-200'}`}
                        >
                          <input
                            type="radio"
                            name="inspectionType"
                            value={opt.value}
                            checked={formData.inspectionType === opt.value}
                            onChange={(e) => setFormData({
                              ...formData,
                              inspectionType: e.target.value,
                              ...(e.target.value === 'standard'
                                ? { gprScope: 'full', gprTargetedLocation: '', gprTargetedReason: '', gprTargetedReasonOther: '', ...(formData.scenario === 'dataInconclusive' ? { scenario: 'noTank' } : {}) }
                                : {})
                            })}
                            className="hidden"
                          />
                          <span className="font-medium text-slate-700">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* GPR Scope – only when GPR is selected */}
                  {formData.inspectionType === 'gpr' && (
                    <div className="space-y-4 animate-slideDown">
                      <label className="block text-sm font-medium text-slate-700">
                        GPR Scope
                      </label>
                      <div className="grid gap-4">
                        {[
                          { value: 'full', label: 'Full Property GPR Sweep' },
                          { value: 'targeted', label: 'Targeted GPR Evaluation' },
                        ].map(opt => (
                          <label
                            key={opt.value}
                            className={`flex items-center p-4 rounded-lg cursor-pointer transition-all border-2
                              ${formData.gprScope === opt.value ? 'bg-cyan-50 border-cyan-500' : 'bg-white/50 border-slate-200 hover:border-cyan-200'}`}
                          >
                            <input
                              type="radio"
                              name="gprScope"
                              value={opt.value}
                              checked={formData.gprScope === opt.value}
                              onChange={(e) => setFormData({
                                ...formData,
                                gprScope: e.target.value,
                                ...(e.target.value === 'full' ? { gprTargetedLocation: '', gprTargetedReason: '', gprTargetedReasonOther: '' } : {})
                              })}
                              className="hidden"
                            />
                            <span className="font-medium text-slate-700">{opt.label}</span>
                          </label>
                        ))}
                      </div>

                      {/* Targeted GPR: location + reason (required when scope = targeted) */}
                      {formData.gprScope === 'targeted' && (
                        <div className="grid gap-4 p-4 bg-slate-50 rounded-lg animate-slideDown">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Location of targeted area *</label>
                            <input
                              type="text"
                              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500"
                              placeholder="e.g. rear yard near driveway"
                              value={formData.gprTargetedLocation}
                              onChange={(e) => setFormData({ ...formData, gprTargetedLocation: e.target.value })}
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">Reason for targeted evaluation *</label>
                            <select
                              className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500 bg-white"
                              value={formData.gprTargetedReason}
                              onChange={(e) => setFormData({ ...formData, gprTargetedReason: e.target.value })}
                            >
                              <option value="">-- Select reason --</option>
                              <option value="metal_detection">Metal detection anomaly</option>
                              <option value="visual">Visual indicator (fill/vent pipe)</option>
                              <option value="prior_docs">Prior documentation</option>
                              <option value="client_request">Client request</option>
                              <option value="other">Other</option>
                            </select>
                            {formData.gprTargetedReason === 'other' && (
                              <input
                                type="text"
                                className="w-full mt-2 px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500"
                                placeholder="Specify reason..."
                                value={formData.gprTargetedReasonOther}
                                onChange={(e) => setFormData({ ...formData, gprTargetedReasonOther: e.target.value })}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Inspection Findings */}
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-slate-700">
                      Inspection Findings*
                    </label>
                    {formData.fullDocumentText && (
                      <div className="p-4 bg-amber-50 border-l-4 border-amber-500 mb-4">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
                          <div>
                            <p className="text-amber-800 font-medium">Custom document format saved</p>
                            <p className="text-amber-700 text-sm mt-1">
                              The report format has been customized in the full preview editor. 
                              Changing the inspection finding will not update the document format until you clear the form.
                            </p>
                            <button
                              onClick={clearForm}
                              className="mt-2 text-sm text-amber-800 hover:text-amber-900 font-medium underline"
                            >
                              Clear form to reset format
                            </button>
                          </div>
                        </div>
                      </div>
                    )}
                    <div className="grid gap-4">
                      {(formData.inspectionType === 'gpr'
                        ? [
                            { value: 'noTank', label: 'No metallic or radar anomalies consistent with a UST detected', icon: CheckCircle2, color: 'text-emerald-500', bgSelected: 'bg-emerald-50', borderSelected: 'border-emerald-500' },
                            { value: 'anomaly', label: 'Metallic anomaly detected; radar not consistent with UST', icon: AlertCircle, color: 'text-amber-500', bgSelected: 'bg-amber-50', borderSelected: 'border-amber-500' },
                            { value: 'ustFound', label: 'Subsurface radar anomaly consistent with UST identified', icon: AlertCircle, color: 'text-red-500', bgSelected: 'bg-red-50', borderSelected: 'border-red-500' },
                            { value: 'dataInconclusive', label: 'Data inconclusive due to site conditions', icon: AlertCircle, color: 'text-slate-500', bgSelected: 'bg-slate-50', borderSelected: 'border-slate-500' },
                          ]
                        : [
                            { value: 'noTank', label: 'No underground storage tank (UST) detected', icon: CheckCircle2, color: 'text-emerald-500', bgSelected: 'bg-emerald-50', borderSelected: 'border-emerald-500' },
                            { value: 'anomaly', label: 'Metallic anomaly detected', icon: AlertCircle, color: 'text-amber-500', bgSelected: 'bg-amber-50', borderSelected: 'border-amber-500' },
                            { value: 'ustFound', label: 'Underground storage tank (UST) identified', icon: AlertCircle, color: 'text-red-500', bgSelected: 'bg-red-50', borderSelected: 'border-red-500' },
                          ]
                      ).map(option => (
                        <label
                          key={option.value}
                          className={`flex items-center p-4 rounded-lg cursor-pointer transition-all
                            ${formData.scenario === option.value 
                              ? `${option.bgSelected} border-2 ${option.borderSelected} shadow-sm` 
                              : 'bg-white/50 border border-slate-200 hover:border-cyan-200 hover:bg-cyan-50'}`}
                        >
                          <input
                            type="radio"
                            name="scenario"
                            value={option.value}
                            checked={formData.scenario === option.value}
                            onChange={(e) => setFormData({ ...formData, scenario: e.target.value })}
                            className="hidden"
                          />
                          <option.icon 
                            className={`h-5 w-5 mr-3 ${option.color}`}
                          />
                          <span className={`font-medium ${formData.scenario === option.value 
                            ? option.color
                            : 'text-slate-700'}`}>
                            {option.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Location Section – Only show if scenario is not "noTank" */}
                  {formData.scenario !== 'noTank' && (
                    <div className="space-y-4">
                      <label className="block text-sm font-medium text-slate-700">
                        Location of detected anomaly or UST (if applicable)
                      </label>
                      <select
                        className="w-full px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white/50"
                        value={formData.anomalyLocation}
                        onChange={(e) => setFormData({ ...formData, anomalyLocation: e.target.value })}
                      >
                        <option value="">-- Select Location --</option>
                        <option value="in the driveway">In the Driveway</option>
                        <option value="in the backyard">In the Backyard</option>
                        <option value="in the left yard">In the Left Yard</option>
                        <option value="in the right yard">In the Right Yard</option>
                        <option value="in the front yard">In the Front Yard</option>
                        <option value="throughout the property">Throughout the Property</option>
                        <option value="custom">Custom Location</option>
                      </select>

                      {formData.anomalyLocation === 'custom' && (
                        <input
                          type="text"
                          className="w-full mt-4 px-4 py-2.5 rounded-lg border border-slate-200 focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 bg-white/50"
                          placeholder="Enter specific location..."
                          value={formData.customLocation}
                          onChange={(e) => setFormData({ ...formData, customLocation: e.target.value })}
                        />
                      )}
                    </div>
                  )}

                  {/* Additional Findings Section */}
                  {formData.scenario !== 'noTank' && (
                    <div className="space-y-4">
                      <button
                        type="button"
                        onClick={() => setShowAdditionalFindings(!showAdditionalFindings)}
                        className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight className={`w-5 h-5 text-slate-400 transition-transform ${showAdditionalFindings ? 'rotate-90' : ''}`} />
                          <span className="font-medium text-slate-700">Additional Findings (Optional)</span>
                        </div>
                        {(formData.findings.vent || formData.findings.fill || formData.findings.distribution) && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-blue-500 bg-blue-50 px-2 py-1 rounded-full">
                              {Object.values(formData.findings).filter(Boolean).length} selected
                            </span>
                          </div>
                        )}
                      </button>

                      {showAdditionalFindings && (
                        <div className="grid gap-3 p-4 bg-slate-50 rounded-lg animate-slideDown">
                          {[
                            { key: 'vent', label: 'Vent Pipe Found' },
                            { key: 'fill', label: 'Fill Pipe Found' },
                            { key: 'distribution', label: 'Distribution Lines Found' }
                          ].map(finding => (
                            <label
                              key={finding.key}
                              className={`flex items-center p-3 rounded-lg cursor-pointer transition-all
                                ${formData.findings[finding.key] 
                                  ? 'bg-blue-50 border-2 border-blue-500' 
                                  : 'bg-white border border-slate-200 hover:bg-blue-50'}`}
                            >
                              <input
                                type="checkbox"
                                checked={formData.findings[finding.key]}
                                onChange={(e) => setFormData({
                                  ...formData,
                                  findings: {
                                    ...formData.findings,
                                    [finding.key]: e.target.checked
                                  }
                                })}
                                className="h-4 w-4 text-blue-500 rounded border-slate-300 focus:ring-blue-500"
                              />
                              <span className="ml-3 font-medium text-slate-700">
                                {finding.label}
                              </span>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Limitations Section */}
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-sm font-medium text-slate-700">
                        Limitations (Optional)
                      </label>
                      <button
                        type="button"
                        onClick={() => addLimitation('', '')}
                        className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                      >
                        <Plus className="w-4 h-4" />
                        Add Limitation
                      </button>
                    </div>

                    {formData.limitations.map((limitation) => (
                      <div key={limitation.id} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 p-4 bg-slate-50 rounded-lg">
                        {/* Limitation Type */}
                        <div className="flex-1 space-y-2">
                          <select
                            className="w-full px-3 py-2 rounded-md border border-slate-200"
                            value={limitation.type}
                            onChange={(e) => updateLimitation(limitation.id, 'type', e.target.value)}
                          >
                            <option value="">Select type...</option>
                            {limitationTypes.map(type => (
                              <option key={type.value} value={type.value}>{type.label}</option>
                            ))}
                            <option value="custom">Custom</option>
                          </select>
                          {limitation.type === 'custom' && (
                            <input
                              type="text"
                              placeholder="Enter custom type"
                              value={limitation.customType || ''}
                              onChange={(e) => updateLimitation(limitation.id, 'customType', e.target.value)}
                              className="w-full px-3 py-2 rounded-md border border-slate-200"
                            />
                          )}
                        </div>

                        {/* Limitation Location */}
                        <div className="flex-1 space-y-2">
                          <select
                            className="w-full px-3 py-2 rounded-md border border-slate-200"
                            value={limitation.location}
                            onChange={(e) => updateLimitation(limitation.id, 'location', e.target.value)}
                          >
                            <option value="">Select location...</option>
                            {locationOptions.map(loc => (
                              <option key={loc.value} value={loc.value}>{loc.label}</option>
                            ))}
                            <option value="custom">Custom</option>
                          </select>
                          {limitation.location === 'custom' && (
                            <input
                              type="text"
                              placeholder="Enter custom location"
                              value={limitation.customLocation || ''}
                              onChange={(e) => updateLimitation(limitation.id, 'customLocation', e.target.value)}
                              className="w-full px-3 py-2 rounded-md border border-slate-200"
                            />
                          )}
                        </div>

                        <button
                          type="button"
                          onClick={() => removeLimitation(limitation.id)}
                          className="self-center p-2 text-slate-400 hover:text-red-500 bg-white rounded-full shadow-sm border border-slate-200"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Prewritten Notes Section */}
                  <PrewrittenNotes
                    currentScenario={formData.scenario}
                    onNoteSelect={(note) => {
                      // If there's existing custom text, append with a space
                      const prefix = formData.customText ? formData.customText.trim() + " " : "";
                      setFormData({
                        ...formData,
                        customText: prefix + note
                      });
                    }}
                    className="mt-6"
                  />

                  {/* Custom Text Section with ghost text context */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="block text-sm font-medium text-slate-700">
                        Additional Notes & Limitations Preview
                      </label>
                      <button
                        type="button"
                        onClick={() => {
                          setPreviewDraft({
                            ...formData,
                            customText: formData.customText,
                            fullDocumentText: formData.fullDocumentText
                          });
                          setShowFullPreview(true);
                        }}
                        className="text-sm text-cyan-600 hover:text-cyan-700 flex items-center gap-1"
                      >
                        <Maximize2 className="w-4 h-4" /> Preview Editor (Advanced)
                      </button>
                    </div>
                    <div className="relative">
                      <div className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/50 min-h-[120px]">
                        {/* Preceding text in ghost color (for GPR full no UST, notes go after the finding, before "While non-invasive...") */}
                        <div className="text-slate-400 font-light">
                          {formData.fullDocumentText ? (
                            formData.fullDocumentText.split(/(?=Utilizing electronic)/)[0]
                          ) : formData.scenario === "noTank" ? (
                            formData.inspectionType === "gpr" && formData.gprScope !== "targeted"
                              ? "...to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). We did not detect any anomalies with our metal detection equipment that match the size of a UST. No metallic responses or hyperbolic radar reflections consistent with the size and cross-sectional shape of an underground storage tank were observed in the scanned areas."
                              : "...to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). We did not detect any anomalies with our metal detection equipment that match the size of a UST."
                          ) : formData.scenario === "anomaly" ? (
                            `...to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). A metallic anomaly, similar in size to an underground storage tank (UST), was detected${formData.anomalyLocation ? " " + (formData.anomalyLocation === "custom" ? formData.customLocation : formData.anomalyLocation) : ""}.`
                          ) : formData.scenario === "ustFound" ? (
                            `...to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). An underground storage tank (UST) was identified${formData.anomalyLocation ? " " + (formData.anomalyLocation === "custom" ? formData.customLocation : formData.anomalyLocation) : ""}.`
                          ) : formData.scenario === "dataInconclusive" ? (
                            "...to identify existing vent pipes, fill pipes, and fuel distribution lines associated with an underground storage tank (UST). Site conditions prevented a definitive conclusion regarding the presence of an underground storage tank (UST)."
                          ) : (
                            ""
                          )}
                        </div>

                        {/* Custom text input with real-time preview */}
                        <div className="relative my-2">
                          <textarea
                            className="w-full bg-transparent focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 resize-none text-blue-600 min-h-[2.5rem] overflow-hidden"
                            placeholder="Your additional notes will appear here..."
                            value={formData.customText || ''}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                customText: newValue,
                                // Only clear fullDocumentText if we don't have any custom edits
                                fullDocumentText: prev.fullDocumentText || ''
                              }));
                              // Auto-expand textarea
                              e.target.style.height = 'inherit';
                              e.target.style.height = `${e.target.scrollHeight}px`;
                            }}
                            style={{ border: 'none', outline: 'none' }}
                            rows={1}
                          />
                        </div>

                        {/* Show limitations only if no fullDocumentText exists */}
                        {!formData.fullDocumentText && formData.limitations?.length > 0 && (
                          <div className="text-red-600 font-medium my-2">
                            {generateLimitationsText()}
                          </div>
                        )}

                        {/* Following text in ghost color (matches conclusion logic: standard / GPR full / GPR targeted / dataInconclusive) */}
                        <div className="text-slate-400 font-light">
                          {formData.scenario === "noTank" && (
                            formData.inspectionType === "gpr" && formData.gprScope === "targeted"
                              ? "The evaluated anomaly did not exhibit radar characteristics consistent with an underground storage tank. This conclusion applies only to the defined evaluation area."
                              : formData.inspectionType === "gpr"
                                ? "While non-invasive detection methods are effective, no method can guarantee the complete absence of buried objects."
                                : "This does not guarantee the absence of USTs on the property; but it is unlikely."
                          )}
                          {formData.scenario === "anomaly" && (
                            "We recommend requesting OPRA records for any information on previously decommissioned oil tanks. If no records are found, proceed with an intrusive investigation."
                          )}
                          {formData.scenario === "ustFound" && (
                            formData.inspectionType === "gpr" && formData.gprScope === "targeted"
                              ? "A distinct hyperbolic reflection consistent with the size and cross-sectional shape of an underground storage tank was observed within the defined evaluation area. Further intrusive verification is recommended."
                              : "This UST may pose a risk of contamination if not addressed. We strongly recommend the removal or proper decommissioning of this UST, along with soil testing for potential contamination."
                          )}
                          {formData.scenario === "dataInconclusive" && (
                            "Follow-up evaluation may be necessary."
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Add a helper text */}
                    <p className="text-xs text-slate-500 mt-1">
                      ℹ️ This preview shows your additional notes in blue and limitations in red
                    </p>
                  </div>

                  {/* Image Upload Section */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <label className="block text-sm font-medium text-slate-700">
                        Site Photos (Optional)
                      </label>
                      {imagePreviews.length > 0 && (
                        <button
                          type="button"
                          onClick={clearAllImages}
                          className="text-sm text-red-500 hover:text-red-600"
                        >
                          Clear All Photos
                        </button>
                      )}
                    </div>
                    <div
                      className="flex flex-col items-center p-6 border-2 border-dashed border-slate-300 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors cursor-pointer"
                      onDrop={handleDrop}
                      onDragOver={handleDragOver}
                      onDragEnter={handleDragEnter}
                      onDragLeave={handleDragLeave}
                    >
                      {imagePreviews.length > 0 ? (
                        <div className="space-y-4 w-full">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {imagePreviews.map((preview, index) => (
                              <div key={index} className="relative group">
                                <img 
                                  src={preview} 
                                  alt={`Photo preview ${index + 1}`} 
                                  className="w-full h-48 object-cover rounded-lg shadow-md"
                                />
                                {/* Add progress indicator */}
                                {imageUploadProgress[`${Date.now()}-${imageFiles[index]?.name}`] !== 'complete' && 
                                 imageUploadProgress[`${Date.now()}-${imageFiles[index]?.name}`] !== undefined && (
                                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                                    {imageUploadProgress[`${Date.now()}-${imageFiles[index]?.name}`] === 'error' ? (
                                      <div className="text-red-500">Upload failed</div>
                                    ) : (
                                      <div className="text-white">
                                        Uploading: {imageUploadProgress[`${Date.now()}-${imageFiles[index]?.name}`]}%
                                      </div>
                                    )}
                                  </div>
                                )}
                                <div className="absolute top-2 right-2 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <button
                                    type="button"
                                    onClick={() => handleEditImage(index)}
                                    className="p-1 bg-white text-blue-500 rounded-full hover:bg-blue-50"
                                    title="Edit image"
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => removeImage(index)}
                                    className="p-1 bg-red-500 text-white rounded-full hover:bg-red-600"
                                    title="Remove image"
                                  >
                                    <X className="h-4 w-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                          <label className="cursor-pointer block text-center">
                            <span className="mt-2 text-sm font-medium text-blue-500 hover:text-blue-600">
                              Add More Photos
                            </span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              multiple
                              onChange={handleFileInputChange}
                            />
                          </label>
                        </div>
                      ) : (
                        <>
                          <Upload className="h-12 w-12 text-slate-400 mb-4" />
                          <label className="cursor-pointer">
                            <span className="mt-2 block text-sm font-medium text-blue-500 hover:text-blue-600">
                              Drop photos here or click to choose
                            </span>
                            <input
                              type="file"
                              className="hidden"
                              accept="image/*"
                              multiple
                              onChange={handleFileInputChange}
                            />
                          </label>
                          <p className="mt-1 text-sm text-slate-500">
                            Recommended: Take photos in landscape orientation. Images will be automatically optimized.
                          </p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Client Search and Inspector Search Components */}
                  <div className="space-y-6 pt-6 border-t border-slate-200">
                    {/* Tab Selector */}
                    <div className="flex border-b border-slate-200">
                      <button
                        type="button"
                        onClick={() => setSearchActiveTab('clients')}
                        className={`py-2 px-4 font-medium text-sm ${
                          searchActiveTab === 'clients'
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          Client Access
                        </div>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSearchActiveTab('inspectors')}
                        className={`py-2 px-4 font-medium text-sm ${
                          searchActiveTab === 'inspectors'
                            ? 'text-blue-600 border-b-2 border-blue-600'
                            : 'text-slate-500 hover:text-slate-700'
                        }`}
                      >
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-2" />
                          Inspector Collaboration
                        </div>
                      </button>
                    </div>

                    {/* Tab Content */}
                    <div className="space-y-4">
                      {searchActiveTab === 'clients' && (
                        <ClientSearch 
                          onClientSelect={handleClientSelect}
                          selectedClients={selectedClients}
                        />
                      )}
                      
                      {searchActiveTab === 'inspectors' && (
                        <InspectorSearch 
                          onInspectorSelect={handleInspectorSelect}
                          selectedInspectors={selectedInspectors}
                        />
                      )}
                    </div>
                  </div>

                  {/* Add Resources Component */}
                  <ReportResources
                    resources={formData.resources}
                    onChange={(resources) => setFormData({ ...formData, resources })}
                    disabled={isSubmitting || isLoading}
                  />

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-4 pt-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white font-medium rounded-lg 
                        ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:from-blue-600 hover:to-cyan-600'} 
                        transition-all`}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center justify-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" />
                          {editingReportId ? 'Updating...' : 'Generating...'}
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          {editingReportId ? (
                            <>
                              <PencilIcon className="w-4 h-4" />
                              Update Report
                            </>
                          ) : (
                            <>
                              <ArrowUpCircle className="w-4 h-4" />
                              Generate Report
                            </>
                          )}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={saveDraft}
                      className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200 transition-colors"
                    >
                      Save Draft
                    </button>
                    <button
                      type="button"
                      onClick={() => clearForm({ shouldConfirm: true, deleteImages: true, clearEditMode: true })}
                      className="flex-1 px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-lg hover:bg-slate-200 transition-colors"
                      title="Clear form with confirmation"
                    >
                      Clear Form
                    </button>
                  </div>
                </form>

                {/* Recent Reports section */}
                <div className="mt-8 pt-6 border-t border-slate-200">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-medium text-slate-700">Recent Reports</h3>
                      <button
                        onClick={() => navigate('/deleted-reports')}
                        className="inline-flex items-center gap-1 text-sm px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg transition-colors"
                        title="View deleted reports"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        <span>Trash</span>
                      </button>
                    </div>
                    <div className="w-full sm:w-auto">
                      <div className="relative flex items-center">
                        <Search className="absolute left-3 h-5 w-5 text-slate-400" />
                        <input
                          type="text"
                          placeholder="Search by name, address, or date..."
                          className="w-full sm:w-80 pl-10 pr-4 py-2 rounded-lg border border-slate-200 focus:ring-2 focus:ring-blue-500"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {isSearching && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        Tip: Try searching by client name, street name, or month (e.g., "January")
                      </p>
                    </div>
                  </div>
                  <div className="space-y-2 min-h-[200px]"> {/* Added min-height */}
                    {isLoading && !isSearching ? (
                      <div className="h-[200px] flex items-center justify-center"> {/* Fixed height loading state */}
                        <p className="text-slate-500">Loading reports...</p>
                      </div>
                    ) : recentReports.length === 0 ? (
                      <div className="h-[200px] flex items-center justify-center"> {/* Fixed height empty state */}
                        <p className="text-slate-500">
                          {searchTerm ? `No reports found matching "${searchTerm}"` : 'No reports found'}
                        </p>
                      </div>
                    ) : (
                      recentReports
                        .filter(report => {
                          if (!searchTerm) return true;
                          const searchLower = searchTerm.toLowerCase();
                          return (
                            report.propertyAddress?.toLowerCase().includes(searchLower) ||
                            report.clientName?.toLowerCase().includes(searchLower) ||
                            formatLongDate(report.dateOfInspection).toLowerCase().includes(searchLower) ||
                            formatShortDate(report.dateOfInspection).toLowerCase().includes(searchLower)
                          );
                        })
                        .map(report => (
                          <div 
                            key={report.id}
                            className={`p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors border-l-4 mx-auto w-full ${
                              report.scenario === 'ustFound' 
                                ? 'border-red-500' 
                                : report.scenario === 'anomaly'
                                  ? 'border-yellow-500'
                                  : 'border-green-500'
                            }`}
                          >
                            {/* Remove the top-right collaborator badge */}
                            {/* Mobile Layout */}
                            <div className="sm:hidden">
                              {/* First row: Name and address - CLICKABLE */}
                              <div 
                                className="mb-3 cursor-pointer" 
                                onClick={(e) => openReportDetails(report.id, e)}
                              >
                                <div className="flex items-center">
                                  <p className="font-medium text-slate-900">{report.clientName || 'No name'}</p>
                                  {/* Add unread messages indicator */}
                                  {unreadMessagesMap[report.id] && unreadMessagesMap[report.id] > 0 && (
                                    <span className="ml-1 relative flex h-3 w-3">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                    </span>
                                  )}
                                  {report.isCollaborator && (
                                    <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                                      Collaborator
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 truncate mt-1">
                                  {removeUSA(report.propertyAddress) || 'No address'}
                                </p>
                                {report.updatedBy && report.createdBy !== report.updatedBy && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Last edited by {report.lastModifiedBy || 'collaborator'}
                                  </p>
                                )}
                                {/* Display collaborators on mobile view */}
                                {report.collaborators && report.collaborators.length > 0 && (
                                  <p className="text-xs text-blue-600 mt-1 flex items-center">
                                    <Users className="h-3 w-3 mr-1" />
                                    Collaborators: {report.collaborators.map(c => c.displayName).join(', ')}
                                  </p>
                                )}
                              </div>
                              
                              {/* Second row: Date on left, buttons on right - NOT CLICKABLE */}
                              <div className="flex justify-between items-center">
                                <span className="text-sm text-slate-500 whitespace-nowrap">
                                  {formatShortDate(report.dateOfInspection, true)}
                                </span>
                                
                                <div className="flex items-center space-x-1">
                                  <button
                                    onClick={() => handleShare(report)}
                                    className="p-1.5 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="Share Report"
                                  >
                                    <Share2 className="h-4 w-4" />
                                  </button>
                                  
                                  {/* New AC Copy dropdown button - Mobile view */}
                                  <div className="relative group">
                                    <button
                                      className="p-1.5 text-gray-600 hover:text-blue-600 transition-colors flex items-center"
                                      title="AC Copy Options"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                    
                                    {/* Dropdown content */}
                                    <div className="absolute right-0 mt-1 w-48 bg-white shadow-lg rounded-md py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                      <button
                                        onClick={() => copyReportLink(report)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                      >
                                        <LinkIcon className="h-3.5 w-3.5 mr-2" />
                                        Share Report Link
                                      </button>
                                      <button
                                        onClick={() => copyPortalLink(report)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-2" />
                                        Create Account Portal
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <button
                                    onClick={() => handleOpenClientModal(report)}
                                    className="p-1.5 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="Update Client Access"
                                  >
                                    <Users className="h-4 w-4" />
                                  </button>
                                  
                                  <button
                                    onClick={() => togglePaidStatus(report.id)}
                                    className={`p-1.5 transition-colors ${report.isPaid ? 'text-green-600' : 'text-red-600'} active:opacity-70 hover:text-green-600`}
                                    title={report.isPaid ? "Mark as Unpaid" : "Mark as Paid"}
                                  >
                                    <DollarSign className="h-4 w-4" />
                                  </button>
                                  {/* Only showing disabled delete button for collaborators, visible for everyone */}
                                  <button
                                    onClick={(e) => !report.isCollaborator && deleteReport(report.id, e)}
                                    className={`p-1.5 transition-colors ${
                                      report.isCollaborator 
                                        ? 'text-gray-300 cursor-not-allowed' 
                                        : 'text-gray-600 hover:text-red-600'
                                    }`}
                                    title={report.isCollaborator ? "Only report creators can delete reports" : "Delete Report"}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => loadReport(report, true)}
                                    className="p-1.5 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="Edit Report"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setViewModalReport(report)}
                                    className="flex items-center gap-1 p-1.5 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="View Count Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                    <span className="text-sm">{viewCounts[report.id] ?? report.viewCount ?? 0}</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Desktop Layout */}
                            <div className="hidden sm:flex flex-row justify-between items-center">
                              <div className="flex-1 min-w-0 cursor-pointer" 
                                onClick={(e) => openReportDetails(report.id, e)}
                              >
                                <div className="flex items-center">
                                  <p className="font-medium text-slate-900">{report.clientName || 'No name'}</p>
                                  {/* Add unread messages indicator */}
                                  {unreadMessagesMap[report.id] && unreadMessagesMap[report.id] > 0 && (
                                    <span className="ml-1 relative flex h-3 w-3">
                                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                                    </span>
                                  )}
                                  {report.isCollaborator && (
                                    <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
                                      Collaborator
                                    </span>
                                  )}
                                </div>
                                <p className="text-sm text-slate-600 truncate mt-1">
                                  {removeUSA(report.propertyAddress) || 'No address'}
                                </p>
                                {report.updatedBy && report.createdBy !== report.updatedBy && (
                                  <p className="text-xs text-slate-500 mt-1">
                                    Last edited by {report.lastModifiedBy || 'collaborator'}
                                  </p>
                                )}
                                {/* Display collaborators */}
                                {report.collaborators && report.collaborators.length > 0 && (
                                  <p className="text-xs text-blue-600 mt-1 flex items-center">
                                    <Users className="h-3 w-3 mr-1" />
                                    Collaborators: {report.collaborators.map(c => c.displayName).join(', ')}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-3 w-auto">
                                <span 
                                  className="text-sm text-slate-500 whitespace-nowrap cursor-pointer" 
                                  onClick={(e) => openReportDetails(report.id, e)}
                                >
                                  {formatShortDate(report.dateOfInspection)}
                                </span>
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => handleShare(report)}
                                    className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="Share Report"
                                  >
                                    <Share2 className="h-4 w-4" />
                                  </button>
                                  
                                  {/* New AC Copy dropdown button - Desktop view */}
                                  <div className="relative group">
                                    <button
                                      className="p-2 text-gray-600 hover:text-blue-600 transition-colors flex items-center"
                                      title="AC Copy Options"
                                    >
                                      <Copy className="h-4 w-4" />
                                    </button>
                                    
                                    {/* Dropdown content */}
                                    <div className="absolute right-0 mt-1 w-48 bg-white shadow-lg rounded-md py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                                      <button
                                        onClick={() => copyReportLink(report)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                      >
                                        <LinkIcon className="h-3.5 w-3.5 mr-2" />
                                        Share Report Link
                                      </button>
                                      <button
                                        onClick={() => copyPortalLink(report)}
                                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5 mr-2" />
                                        Create Account Portal
                                      </button>
                                    </div>
                                  </div>
                                  
                                  <button
                                    onClick={() => handleOpenClientModal(report)}
                                    className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="Update Client Access"
                                  >
                                    <Users className="h-4 w-4" />
                                  </button>
                                  
                                  <button
                                    onClick={() => togglePaidStatus(report.id)}
                                    className={`p-2 transition-colors ${report.isPaid ? 'text-green-600' : 'text-red-600'} active:opacity-70 hover:text-green-600`}
                                    title={report.isPaid ? "Mark as Unpaid" : "Mark as Paid"}
                                  >
                                    <DollarSign className="h-4 w-4" />
                                  </button>
                                  {/* Only showing disabled delete button for collaborators, visible for everyone */}
                                  <button
                                    onClick={(e) => !report.isCollaborator && deleteReport(report.id, e)}
                                    className={`p-2 transition-colors ${
                                      report.isCollaborator 
                                        ? 'text-gray-300 cursor-not-allowed' 
                                        : 'text-gray-600 hover:text-red-600'
                                    }`}
                                    title={report.isCollaborator ? "Only report creators can delete reports" : "Delete Report"}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => loadReport(report, true)}
                                    className="p-2 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="Edit Report"
                                  >
                                    <PencilIcon className="h-4 w-4" />
                                  </button>
                                  <button
                                    onClick={() => setViewModalReport(report)}
                                    className="flex items-center gap-1 p-2 text-gray-600 hover:text-blue-600 transition-colors"
                                    title="View Count Details"
                                  >
                                    <Eye className="h-4 w-4" />
                                    <span className="text-sm">{viewCounts[report.id] ?? report.viewCount ?? 0}</span>
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))
                    )}
                    {!isLoading && hasMore && recentReports.length > 0 && (
                      <button
                        onClick={loadMoreReports}
                        className="w-full py-3 text-center text-blue-500 hover:text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
                      >
                        Show More Reports ({recentReports.length} of {totalReports})
                      </button>
                    )}
                  </div>
                </div>

                {/* Drafts section */}
                <DraftsSection 
                  user={user}
                  drafts={drafts}
                  setDrafts={setDrafts}
                  setPreviewDraft={setPreviewDraft}
                  loadReport={loadReport}
                  deleteDraft={deleteDraft}
                  renameDraft={renameDraft}
                />

                {/* Footer Logo */}
                <div className="mt-12 pt-6 border-t border-slate-200 flex items-center justify-between">
                  <img 
                    src="/leo.png" 
                    alt="Site Logo" 
                    className="h-8 object-contain opacity-80"
                  />
                  <span className="text-sm text-slate-500 italic">
                    Powered by many sleepless nights ✨
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Add the MobileActionBar here */}
        <MobileActionBar clearForm={clearForm} />
        
        {/* Preview Draft Modal */}
        {previewDraft && (
          <FormPreviewModal 
            draft={previewDraft} 
            onClose={() => setPreviewDraft(null)} 
            setFormData={setFormData}
            generateBodyText={generateBodyText}
            generateLimitationsText={generateLimitationsText}
          />
        )}

        {/* Add the ImageEditor modal */}
        {editingImage && (
          <ImageEditor
            imageUrl={editingImage}
            onSave={handleSaveEdit}
            onClose={() => {
              setEditingImage(null);
              setEditingImageIndex(null);
            }}
          />
        )}

        {/* Add the ChangelogModal */}
        {showChangelog && (
          <ChangelogModal
            showChangelog={showChangelog}
            setShowChangelog={setShowChangelog}
            changelogContent={changelogContent}
          />
        )}
        
        {/* Add ViewCountModal */}
        {viewModalReport && (
          <ViewCountModal
            report={recentReports.find(r => r.id === viewModalReport.id) || viewModalReport}
            onClose={() => setViewModalReport(null)}
            user={user}
            resetViewCount={resetViewCount}
          />
        )}
      </div>
      
      {/* Client Sharing Modal */}
      {shareClientModalReport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h3 className="text-lg font-medium text-gray-900">
                Share with Portal Clients
              </h3>
              <button
                onClick={() => {
                  setShareClientModalReport(null);
                  setSelectedClients([]);
                  setSelectedInspectors([]);
                }}
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4">
              <p className="text-sm text-gray-500 mb-4">
                Update which clients have access to this report in the client portal.
              </p>
              
              <div className="mb-4">
                <p className="font-medium">Report: {shareClientModalReport.clientName}</p>
                <p className="text-sm text-gray-500">
                  {shareClientModalReport.propertyAddress}
                </p>
              </div>
              
              <div className="space-y-4">
                {/* Tab Selector */}
                <div className="flex border-b border-slate-200">
                  <button
                    type="button"
                    onClick={() => setSearchActiveTab('clients')}
                    className={`py-2 px-4 font-medium text-sm ${
                      searchActiveTab === 'clients'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Client Access
                    </div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setSearchActiveTab('inspectors')}
                    className={`py-2 px-4 font-medium text-sm ${
                      searchActiveTab === 'inspectors'
                        ? 'text-blue-600 border-b-2 border-blue-600'
                        : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    <div className="flex items-center">
                      <Users className="h-4 w-4 mr-2" />
                      Inspector Collaboration
                    </div>
                  </button>
                </div>

                {/* Tab Content */}
                <div className="space-y-4">
                  {searchActiveTab === 'clients' && (
                    <ClientSearch
                      onClientSelect={handleClientSelect}
                      selectedClients={selectedClients}
                    />
                  )}
                  
                  {searchActiveTab === 'inspectors' && (
                    <InspectorSearch 
                      onInspectorSelect={handleInspectorSelect}
                      selectedInspectors={selectedInspectors}
                    />
                  )}
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setShareClientModalReport(null);
                  setSelectedClients([]);
                  setSelectedInspectors([]);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-3 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateClients}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                disabled={isUpdatingClients}
              >
                {isUpdatingClients ? (
                  <span className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </span>
                ) : (
                  'Update Client Access'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      {showReportModal && selectedReportId && (
        <AdminReportModal
          reportId={selectedReportId}
          onClose={closeReportModal}
        />
      )}
    </>
  );
};

export default function ModernFormWithErrorBoundary() {
  return (
    <ErrorBoundary>
      <ModernForm />
    </ErrorBoundary>
  );
}